import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink as RRNavLink } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { push } from "react-router-redux";
import GoogleTranslate from "../components/GoogleTranslate"; // keep your path
import CommandPalette from "../components/CommandPalette";
import Breadcrumbs from "../components/Breadcrumbs";
import EnhancedTooltip from "../components/EnhancedTooltip";
import DarkModeToggle from "../components/DarkModeToggle";
import { ToastProvider } from "../components/ToastProvider";
import Minesweeper from "../components/Minesweeper";
import FlappyBird from "../components/FlappyBird";
import Snake from "../components/Snake";
import GamePicker from "../components/GamePicker";
import { authLogout } from "../actions/auth";
import { requestProUpgrade, resetProUpgrade } from "../actions/license";
import { fetchCustomerProfile, resetCustomerProfile } from "../actions/customerProfile";
import { fetchMachinesNightscan } from "../actions/machines";
import { fetchOpenTicketsCount } from "../actions/supporttickets";
import { fetchOpenIncidencesCount } from "../actions/incidences";
import { canAccess, hasAccessToAny, isFeatureLocked } from "../utils/permissions";
import FloatingToolsButton from '../components/FloatingToolsButton';
import * as XLSX from "xlsx";

// lucide-react icons
import {
  LayoutGrid,
  Menu as MenuIcon,
  LogOut,
  User as UserIcon,
  Hotel,
  CreditCard,
  Unlock,
  Receipt,
  Monitor,
  QrCode,
  Smartphone,
  Route,
  Info,
  Database,
  Wifi,
  DoorOpen,
  Lock,
  CalendarCheck2,
  PencilRuler,
  Newspaper,
  Gem,
  Download,
  Filter,
  Star,
  BarChart2,
  PieChart,
  Calculator,
  Target,
  HelpCircle,
  BookOpen,
  MessageSquare,
  Crown,
  FileSignature,
  Gamepad2,
  SlidersHorizontal,
  UserCog,
  ArrowRight,
  ArrowLeft,
  Key,
  KeyRound,
  KeySquare,
  Search,
  Headset,
  Mountain,
  Mail,
  Tag,
  Ticket,
  AlertTriangle,
  ShieldCheck,
  Wrench,
  Phone,
  MapPin,
  Globe,
  Building2,
  Users,
  Code,
  ChevronDown,
  Briefcase,
  Box,
  FileText,
  Replace,
  BatteryFull,
  ClipboardCheck,
  RefreshCw,
  Timer
} from "lucide-react";

/**
 * Lightweight styles. Inject once on mount.
 */
const injectStyles = () => {
  // create or update style tag so HMR changes are applied without reload
  let style = document.getElementById("app-shell-styles");
  const css = `
/* ==================== VARIABLES ==================== */
:root {
  --vw-dark: #0f172a;
  --vw-dark-900: #0b1223;
  --vw-dark-gradient: linear-gradient(to bottom, #0f172a, #01315f);
  --vw-accent: #1e293b;
  --sidebar-w: 280px;
  --sidebar-collapsed-w: 72px;
  --sidebar-icons-w: 72px;
  --sidebar-sub-w: 260px;
  --topbar-h: 64px;
  --mobile-bottom-nav-h: 70px;
  --danger: #ef4444;
  --focus-ring: 0 0 0 3px rgba(59, 130, 246, 0.5);
  --focus-ring-light: 0 0 0 3px rgba(255, 255, 255, 0.3);
  
  /* Theme colors - Light mode (default) */
  --bg-primary: #f7f8fb;
  --bg-secondary: #ffffff;
  --text-primary: #111827;
  --text-secondary: #6b7280;
  --border-color: #e5e7eb;
  
  /* Topbar colors - Light mode */
  --topbar-bg: #ffffff;
  --topbar-text: #111827;
  --topbar-border: #e5e7eb;
  --topbar-btn-border: rgba(0, 0, 0, 0.1);
  --topbar-btn-hover: rgba(0, 0, 0, 0.05);
  --topbar-icon-color: #6b7280;
  
  /* Sidebar colors - Light mode */
  --sidebar-bg: #ffffff;
  --sidebar-icons-bg: #f8f9fa;
  --sidebar-border: #e5e7eb;
  --sidebar-text: #111827;
  --sidebar-text-secondary: #6b7280;
  --sidebar-icon-color: #6b7280;
  --sidebar-icon-active: #111827;
  --sidebar-btn-hover: rgba(0, 0, 0, 0.05);
  --sidebar-btn-active: rgba(0, 0, 0, 0.08);
  --sidebar-submenu-bg: #ffffff;
  --sidebar-submenu-label: #6b7280;
  --sidebar-submenu-border: #e5e7eb;
}

/* Dark mode variables */
.dark-mode {
  --bg-primary: #0f172a;
  --bg-secondary: #1e293b;
  --text-primary: #f9fafb;
  --text-secondary: #d1d5db;
  --border-color: #374151;
  --dark-gradient: linear-gradient(135deg,#022040,#0a4080);
  
  /* Topbar colors - Dark mode */
  --topbar-bg: var(--dark-gradient);
  --topbar-text: #ffffff;
  --topbar-border: rgba(255, 255, 255, 0.08);
  --topbar-btn-border: rgba(255, 255, 255, 0.2);
  --topbar-btn-hover: rgba(255, 255, 255, 0.1);
  --topbar-icon-color: #ffffff;
  
  /* Sidebar colors - Dark mode */
  --sidebar-bg: var(--dark-gradient);
  --sidebar-icons-bg: rgba(15, 23, 42, 0.95);
  --sidebar-border: rgba(255, 255, 255, 0.09);
  --sidebar-text: #ffffff;
  --sidebar-text-secondary: #cbd5e1;
  --sidebar-icon-color: #9ca3af;
  --sidebar-icon-active: #ffffff;
  --sidebar-btn-hover: rgba(255, 255, 255, 0.1);
  --sidebar-btn-active: rgba(255, 255, 255, 0.08);
  --sidebar-submenu-bg: linear-gradient(to bottom, #0f172a, #01315f);
  --sidebar-submenu-label: #9ca3af;
  --sidebar-submenu-border: rgba(255, 255, 255, 0.05);
}

.dark-mode .shell {
  background-color: #f7f8fb;
}

.dark-mode .breadcrumbs {
  background: var(--dark-gradient);
  border-color: var(--border-color);
}

.dark-mode .breadcrumbs-link,
.dark-mode .breadcrumbs-text {
  color: var(--text-secondary);
}

.dark-mode .breadcrumbs-current {
  color: var(--text-primary);
}

.dark-mode .command-palette {
  background: var(--dark-gradient);
  border: 1px solid var(--border-color);
}

.dark-mode .command-palette-input {
  color: var(--text-primary);
}

.dark-mode .command-palette-input::placeholder {
  color: var(--text-secondary);
}

.dark-mode .command-palette-item-label {
  color: var(--text-primary);
}

.dark-mode .command-palette-item-description {
  color: var(--text-secondary);
}

.dark-mode .command-palette-item:hover,
.dark-mode .command-palette-item.selected {
  background: rgba(255, 255, 255, 0.05);
}

.dark-mode .toast-item {
  background: var(--dark-gradient);
  border-color: var(--border-color);
}

.dark-mode .toast-title {
  color: var(--text-primary);
}

/* ==================== TOPBAR ==================== */
.topbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  height: var(--topbar-h);
  background: var(--topbar-bg);
  color: #fff;
  display: flex;
  align-items: center;
  z-index: 1030;
  box-shadow: 0 2px 8px rgba(0,0,0,.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.topbar__inner {
  width: 100%;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.topbar__left, .topbar__right {
  display: flex;
  align-items: center;
}

.topbar__brand img {
  height: 30px;
  display: block;
}

/* Hide dark logo in light mode, show light logo */
.topbar__brand .logo-dark {
  display: none;
}

.topbar__brand .logo-light {
  display: block;
}

/* Hide light logo in dark mode, show dark logo */
.dark-mode .topbar__brand .logo-dark {
  display: block;
}

.dark-mode .topbar__brand .logo-light {
  display: none;
}

.topbar__brand:focus {
  outline: none;
  box-shadow: var(--focus-ring);
  border-radius: 8px;
}

/* ── platform switcher ───────────────────────────────────────── */

/* ── C: Branded chip + card panel ── */
.psw-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  border-radius: 10px;
  color: var(--topbar-text);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.psw-chip:hover {
  background: var(--topbar-btn-hover);
  transform: translateY(-1px);
}
.psw-chip:active { transform: translateY(0); }
.psw-chip:focus  { outline: none; }
.psw-chip-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #3b82f6; flex-shrink: 0;
}
.psw-chip-chevron {
  transition: transform .2s;
  opacity: .7;
}
.psw-chip-chevron--open { transform: rotate(180deg); }
.psw-chip-backdrop {
  position: fixed; inset: 0; z-index: 1998;
}
.psw-chip-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1999;
  background: var(--topbar-bg);
  border: 1px solid var(--topbar-border);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,.18);
  padding: 8px;
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  animation: psw-fadein .15s ease;
}
@keyframes psw-fadein {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.psw-chip-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 9px;
  text-decoration: none !important;
  color: var(--topbar-text) !important;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .05em;
  transition: background .12s;
}
.psw-chip-card:hover { background: var(--topbar-btn-hover); }
.psw-chip-card-accent {
  width: 4px; height: 28px; border-radius: 4px; flex-shrink: 0;
}
.psw-chip-card-label { flex: 1; }

.btn-ghost {
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  gap: 6px;
}
.btn-ghost:hover {
  background: var(--topbar-btn-hover);
  transform: translateY(-1px);
}
.btn-ghost:active {
  transform: translateY(0);
}
.btn-ghost:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.nightscan-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 10px;
  border-radius: 10px;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.nightscan-btn:hover {
  background: var(--topbar-btn-hover);
  transform: translateY(-1px);
}
.nightscan-btn:active {
  transform: translateY(0);
}
.nightscan-btn:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.nightscan-badge {
  position: absolute;
  top: -6px; right: -6px;
  min-width: 18px; height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--danger);
  color: #fff;
  font-size: 11px;
  border-radius: 999px;
  line-height: 1;
}

.nightscan-badge--loading {
  background: rgba(255,255,255,0.25);
  width: 18px;
  padding: 0;
}

.nightscan-spinner {
  display: block;
  width: 10px; height: 10px;
  border: 2px solid rgba(255,255,255,0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: nightscan-spin 0.7s linear infinite;
}

@keyframes nightscan-spin {
  to { transform: rotate(360deg); }
}

/* Tickets indicator button */
.tickets-indicator-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 10px;
  border-radius: 10px;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.tickets-indicator-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: #3b82f6;
  transform: translateY(-1px);
}
.tickets-indicator-btn:active {
  transform: translateY(0);
}
.tickets-indicator-btn .indicator-badge {
  position: absolute;
  top: -6px; right: -6px;
  min-width: 18px; height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #3b82f6;
  color: #fff;
  font-size: 11px;
  border-radius: 999px;
  line-height: 1;
}

/* Incidences indicator button */
.incidences-indicator-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 10px;
  border-radius: 10px;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.incidences-indicator-btn:hover {
  background: rgba(249, 115, 22, 0.1);
  border-color: #f97316;
  transform: translateY(-1px);
}
.incidences-indicator-btn:active {
  transform: translateY(0);
}
.incidences-indicator-btn .indicator-badge {
  position: absolute;
  top: -6px; right: -6px;
  min-width: 18px; height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f97316;
  color: #fff;
  font-size: 11px;
  border-radius: 999px;
  line-height: 1;
}

.dropdown-toggle:after {
  color: var(--topbar-text) !important;
}

/* Language dropdown styling */
.nav-item.dropdown {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
}

.dropdown-toggle {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
}

.dropdown-toggle::after {
  margin-left: 4px !important;
}

/* Mobile-specific topbar */
.topbar__mobileToggle { display: none; }
@media (max-width: 768px) {
  .topbar__right .btn-ghost.user-btn,
  .topbar__right .btn-ghost.logout-btn { display: none; }
  /* Hide nightscan, tickets, incidences buttons on mobile - they're in Profile menu */
  .nightscan-btn,
  .tickets-indicator-btn,
  .incidences-indicator-btn { display: none !important; }
  .topbar__mobileToggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: 1px solid var(--topbar-btn-border);
    color: var(--topbar-text);
    padding: 6px 10px;
    border-radius: 10px;
    margin-left: 8px;
  }
  .topbar__mobileToggle:hover { background: var(--topbar-btn-hover); }
}

/* Hide list markers in dropdowns */
.dropdown-menu li::marker,
.nav-item::marker {
  display: none;
  content: none;
}

/* Ensure dropdowns appear above other elements */
.dropdown-menu {
  z-index: 1050;
}

.nav-item {
  list-style: none;
}

/* ==================== LAYOUT ==================== */
.shell {
  display: flex;
  min-height: 100vh;
  background: #f7f8fb;
}

/* ==================== SIDEBAR PRO ==================== */
.sidebar-pro {
  position: fixed;
  top: var(--topbar-h);
  left: 0;
  bottom: 0;
  display: flex;
  background: var(--sidebar-bg);
  color: var(--sidebar-text);
  z-index: 1020;
  box-shadow: 2px 0 8px rgba(0,0,0,.15);
  transition: all .25s ease;
}

/* Left icons column */
.sidebar-icons {
  width: var(--sidebar-icons-w);
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--sidebar-bg);
  border-right: 1px solid var(--sidebar-border);
  padding-top: 10px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}
.sidebar-icons:hover {
  scrollbar-color: rgba(150,150,150,0.35) transparent;
}
.sidebar-icons::-webkit-scrollbar { width: 4px; }
.sidebar-icons::-webkit-scrollbar-track { background: transparent; }
.sidebar-icons::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 999px;
  transition: background .2s;
}
.sidebar-icons:hover::-webkit-scrollbar-thumb {
  background: rgba(150,150,150,0.35);
}
.sidebar-icons::-webkit-scrollbar-thumb:hover {
  background: rgba(150,150,150,0.6);
}

.icon-btn {
  background: transparent;
  color: var(--sidebar-icon-color);
  border: none;
  padding: 14px 0;
  cursor: pointer;
  width: 100%;
  min-height: 72px;
  display: flex;
  justify-content: center;
  transition: all .2s ease;
  outline: none;
  flex-direction: column;
  align-items: center;
  position: relative;
}
.icon-btn:hover, .icon-btn.active {
  color: var(--sidebar-icon-active);
  background: var(--sidebar-btn-hover);
}

.icon-btn:focus {
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(59, 130, 246, 0.5);
}

.icon-btn:focus:not(:focus-visible) {
  box-shadow: none;
}

.icon-label {
  font-size: 0.7rem;
  margin-top: 0.25rem;
}

/* Right submenu panel */
.submenu-panel {
  width: 0;
  overflow: hidden;
  background: var(--sidebar-bg);
  transition: width .25s ease;
}
.submenu-panel.open { width: var(--sidebar-sub-w); }

.submenu-content {
  opacity: 0;
  transform: translateX(-10px);
  transition: opacity .25s ease, transform .25s ease;
  padding: 16px 12px;
  height: 100%;
  overflow-y: auto;
}
.submenu-panel.open .submenu-content {
  opacity: 1;
  transform: translateX(0);
}

/* Submenu links */
.submenu-label {
  font-size: 11px;
  text-transform: uppercase;
  color: var(--sidebar-submenu-label);
  padding: 8px 12px 4px;
  margin-top: 8px;
  letter-spacing: .05em;
  border-bottom: 1px solid var(--sidebar-submenu-border);
}

.submenu-content .nav-button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  color: var(--sidebar-text-secondary);
  background: transparent;
  padding: 10px 12px;
  border-radius: 10px;
  text-decoration: none;
  transition: background .12s ease, color .12s ease;
  border: none;
  cursor: pointer;
  text-align: left;
  margin-top: 5px;
}
.submenu-content .nav-button:hover,
.submenu-content .nav-button.active {
  background: var(--sidebar-btn-active);
  color: var(--sidebar-text);
  transform: translateX(4px);
}

.submenu-content .nav-button:active {
  transform: translateX(2px);
}
.submenu-content .nav-button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}
.submenu-content .nav-button svg {
  min-width: 20px;
  opacity: 0.9;
}

/* ── Sidebar bottom PRO upgrade button ── */
.icon-btn--pro {
  color: #f59e0b;
  border-top: 1px solid var(--sidebar-border);
}
.icon-btn--pro:hover,
.icon-btn--pro:focus {
  background: rgba(245, 158, 11, 0.12) !important;
  color: #f97316 !important;
}

/* ── Pro-locked menu items ── */
.nav-button--locked {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  color: var(--sidebar-text-secondary);
  background: transparent;
  padding: 10px 12px;
  border-radius: 10px;
  text-decoration: none;
  border: none;
  cursor: not-allowed;
  text-align: left;
  margin-top: 5px;
  opacity: 0.5;
  position: relative;
}
.nav-button--locked:hover {
  opacity: 0.7;
}
.nav-button--locked svg {
  min-width: 20px;
  opacity: 0.6;
}

.pro-badge-inline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: #fff;
  padding: 1px 5px;
  border-radius: 4px;
  margin-left: auto;
  line-height: 1.3;
  text-transform: uppercase;
  flex-shrink: 0;
}

.lock-icon-inline {
  opacity: 0.6;
  margin-left: 4px;
  flex-shrink: 0;
}

/* Pro License Toast Notification */
.pro-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: #fff;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
  z-index: 10000;
  font-size: 14px;
  animation: proToastSlideUp 0.3s ease-out;
  border: 1px solid rgba(245,158,11,0.3);
  white-space: nowrap;
}
.pro-toast svg {
  color: #f59e0b;
  flex-shrink: 0;
}
.pro-toast strong {
  color: #f59e0b;
}
.pro-toast--mobile {
  bottom: 80px;
}

@keyframes proToastSlideUp {
  from { opacity: 0; transform: translateX(-50%) translateY(16px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* Scrollbars for submenu */
.submenu-content::-webkit-scrollbar { width: 8px; }
.submenu-content::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.25);
  border-radius: 999px;
}
.submenu-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255,255,255,0.5);
}

/* Content shifting (desktop only) */
.content.shifted {
  margin-left: calc(var(--sidebar-icons-w) + var(--sidebar-sub-w));
  max-width: calc(100vw - (var(--sidebar-icons-w) + var(--sidebar-sub-w)));
}
.content.not-shifted {
  margin-left: var(--sidebar-icons-w);
  max-width: calc(100vw - var(--sidebar-icons-w));
}
.content {
  flex: 1;
  padding-top: var(--topbar-h);
  transition: margin-left .25s ease;
  max-width: calc(100vw - var(--sidebar-icons-w));
}

/* ==================== NIGHTSCAN RIGHT DRAWER ==================== */
.rightpanel {
  position: fixed;
  top: var(--topbar-h);
  right: 0;
  bottom: 0;
  width: 360px;
  max-width: 92vw;
  background: #fff;
  color: #0f172a;
  border-left: 1px solid rgba(0,0,0,.1);
  box-shadow: -2px 0 12px rgba(0,0,0,.12);
  transform: translateX(100%);
  transition: transform .22s ease;
  z-index: 1040;
  display: flex;
  flex-direction: column;
}
.rightpanel--open { transform: translateX(0); }
.rightpanel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  background: #f8fafc;
}
.rightpanel__title { font-weight: 600; }
.nightscan-csv-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #022040, #0a4080);
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
}
.nightscan-csv-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.nightscan-csv-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.nightscan-csv-btn--analysis { background: linear-gradient(135deg, #6d28d9, #a855f7); }

/* ===== Nightscan Analysis Modal ===== */
.ns-analysis-overlay {
  position: fixed; inset: 0; z-index: 10500;
  background: rgba(2, 8, 20, 0.55);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
}
.ns-analysis-modal {
  background: #fff; border-radius: 14px;
  width: min(1100px, 100%);
  max-height: calc(100vh - 48px);
  display: flex; flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0,0,0,.35);
}
.ns-analysis-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 12px; padding: 14px 18px;
  background: linear-gradient(135deg, #022040, #0a4080);
  color: #fff;
}
.ns-analysis-header h2 { margin: 0; font-size: 17px; font-weight: 700; color: #fff; }
.ns-analysis-sub { font-size: 12px; opacity: .85; margin-top: 3px; }
.ns-analysis-actions { display: flex; align-items: center; gap: 8px; }
.ns-analysis-close {
  background: rgba(255,255,255,.15); border: none; color: #fff;
  border-radius: 8px; width: 30px; height: 30px; cursor: pointer;
  font-size: 15px; line-height: 1; flex-shrink: 0;
}
.ns-analysis-close:hover { background: rgba(255,255,255,.3); }
.ns-analysis-body { padding: 16px 18px; overflow-y: auto; }
.ns-analysis-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
.ns-analysis-section {
  border: 1px solid rgba(0,0,0,.08); border-radius: 12px; overflow: hidden; background: #fff;
}
.ns-analysis-section__head {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 10px 12px; font-weight: 700; font-size: 13px;
}
.ns-analysis-section--offline .ns-analysis-section__head { background: #fef2f2; color: #b91c1c; }
.ns-analysis-section--noscan .ns-analysis-section__head { background: #fff7ed; color: #c2410c; }
.ns-analysis-section--chronic .ns-analysis-section__head { background: #f8fafc; color: #475569; }
.ns-analysis-count { border-radius: 999px; padding: 2px 10px; font-size: 12px; background: rgba(0,0,0,.08); }
.ns-analysis-section--offline .ns-analysis-count { background: #b91c1c; color: #fff; }
.ns-analysis-section--noscan .ns-analysis-count { background: #c2410c; color: #fff; }
.ns-analysis-list { max-height: 280px; overflow-y: auto; padding: 8px; }
.ns-analysis-item {
  width: 100%; text-align: left; background: #fff;
  border: 1px solid rgba(0,0,0,.07); border-radius: 8px;
  padding: 7px 10px; margin-bottom: 6px; cursor: pointer;
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  transition: all .15s ease; font-size: 12.5px;
}
.ns-analysis-item:hover { border-color: #0a4080; background: rgba(10,64,128,.06); }
.ns-analysis-item__main { min-width: 0; }
.ns-analysis-item__serial { font-weight: 700; }
.ns-analysis-item__customer { color: #475569; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ns-analysis-item__meta { text-align: right; color: #64748b; font-size: 11.5px; white-space: nowrap; flex-shrink: 0; }
.ns-analysis-days { font-weight: 700; color: #b91c1c; }
.ns-analysis-tag {
  display: inline-block; border-radius: 999px; padding: 1px 8px;
  font-size: 10.5px; font-weight: 600; margin-left: 6px;
}
.ns-analysis-tag--offline { background: #fee2e2; color: #b91c1c; }
.ns-analysis-tag--noscan { background: #ffedd5; color: #c2410c; }
.ns-analysis-tag--sleep { background: #e0e7ff; color: #3730a3; }
.ns-analysis-empty { padding: 14px; color: #16a34a; font-size: 12.5px; text-align: center; font-weight: 600; }
.ns-analysis-loading {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  padding: 48px 20px; color: #475569; font-size: 13px; font-weight: 600;
}
.ns-analysis-loading__spinner {
  width: 28px; height: 28px; border-radius: 50%;
  border: 3px solid rgba(109,40,217,.2); border-top-color: #6d28d9;
  animation: nightscan-spin 0.7s linear infinite;
}
.ns-analysis-chronic-note { color: #64748b; font-size: 11.5px; font-weight: 400; }
@media (max-width: 820px) {
  .ns-analysis-overlay { padding: 10px; }
  .ns-analysis-modal { max-height: calc(100vh - 20px); }
  .ns-analysis-grid { grid-template-columns: 1fr; }
}
.rightpanel__body { padding: 12px; overflow-y: auto; }
.rightpanel__item {
  width: 100%;
  text-align: left;
  border: 1px solid rgba(0,0,0,.08);
  background: #fff;
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all .15s ease;
}
.rightpanel__item:hover {
  border-color: #0015ce;
  background: #0312e71a;
  transform: translateX(-4px);
  box-shadow: 0 2px 8px rgba(0, 21, 206, 0.15);
}

.rightpanel__item:active {
  transform: translateX(-2px);
}
.status-badge {
  display: inline-block;
  color: #fff;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
}
.rightpanel__empty {
  padding: 24px;
  color: #64748b;
  text-align: center;
}
.backdrop-right {
  position: fixed;
  top: var(--topbar-h);
  left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,.45);
  z-index: 1035;
}

/* ==================== MOBILE BOTTOM TAB BAR ==================== */
@media (max-width: 768px) {
  .mobile-bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: var(--mobile-bottom-nav-h);
    background: var(--sidebar-bg);
    border-top: 1px solid var(--sidebar-border);
    display: flex;
    align-items: center;
    justify-content: space-around;
    z-index: 1030;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    padding: 0 8px;
    opacity: 1;
  }

  .dark-mode .mobile-bottom-nav {
    background: var(--dark-gradient);
    backdrop-filter: blur(10px);
  }

  .mobile-tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: transparent;
    border: none;
    color: var(--sidebar-icon-color);
    padding: 8px 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 8px;
    position: relative;
    min-width: 0;
  }

  .mobile-tab:active {
    transform: scale(0.95);
  }

  .mobile-tab.active {
    color: var(--sidebar-icon-active);
    background: var(--sidebar-btn-active);
  }

  .mobile-tab-label {
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .mobile-tab-badge {
    position: absolute;
    top: 6px;
    right: 8px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--danger);
    color: #fff;
    font-size: 10px;
    border-radius: 999px;
    line-height: 1;
  }

  /* Mobile menu modal (slides from bottom) */
  .mobile-menu-modal {
    position: fixed;
    bottom: var(--mobile-bottom-nav-h);
    left: 0;
    right: 0;
    max-height: calc(85vh - var(--mobile-bottom-nav-h));
    background: var(--sidebar-bg);
    border-radius: 20px 20px 0 0;
    z-index: 1029;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
  }

  .mobile-menu-modal.open {
    transform: translateY(0);
  }

  .mobile-menu-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--sidebar-border);
    background: var(--sidebar-bg);
    border-radius: 20px 20px 0 0;
  }

  .mobile-menu-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--sidebar-text);
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .mobile-menu-close {
    background: transparent;
    border: none;
    color: var(--sidebar-icon-color);
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;
  }

  .mobile-menu-close:hover {
    background: var(--sidebar-btn-hover);
  }

  .mobile-menu-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: visible;
    padding: 16px;
  }

  /* Language dropdown in mobile needs proper positioning */
  @media (max-width: 768px) {
    .mobile-menu-content .dropdown-menu {
      position: absolute !important;
      top: auto !important;
      bottom: 100% !important;
      left: 50% !important;
      right: auto !important;
      transform: translateX(-50%) translateY(-8px) !important;
      margin: 0 !important;
      z-index: 9999 !important;
      max-height: 300px;
      overflow-y: auto;
      padding: 10rem .5rem;
    }

    .mobile-menu-content .dropdown {
      position: relative !important;
    }
  }

  .mobile-menu-section {
    margin-bottom: 20px;
  }

  .mobile-menu-section-title {
    font-size: 11px;
    text-transform: uppercase;
    color: var(--sidebar-submenu-label);
    padding: 8px 12px 4px;
    letter-spacing: 0.05em;
    border-bottom: 1px solid var(--sidebar-submenu-border);
    margin-bottom: 8px;
  }

  .mobile-menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    color: var(--sidebar-text-secondary);
    background: transparent;
    padding: 14px 12px;
    border-radius: 10px;
    text-decoration: none;
    transition: all 0.2s ease;
    border: none;
    cursor: pointer;
    text-align: left;
    font-size: 15px;
  }

  .mobile-menu-item:active {
    transform: scale(0.98);
  }

  .mobile-menu-item:hover,
  .mobile-menu-item.active {
    background: var(--sidebar-btn-active);
    color: var(--sidebar-text);
  }

  .mobile-menu-item svg {
    min-width: 22px;
    opacity: 0.9;
  }

  /* Mobile locked items */
  .mobile-menu-item--locked {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .mobile-menu-item--locked:hover {
    background: transparent;
    opacity: 0.65;
  }
  .mobile-menu-item--locked:active {
    transform: none;
  }

  /* Profile menu specific styles */
  .mobile-profile-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px;
    background: var(--sidebar-bg);
    border-bottom: 1px solid var(--sidebar-border);
  }

  .mobile-profile-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: var(--sidebar-btn-active);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--sidebar-icon-active);
  }

  .mobile-profile-info {
    flex: 1;
  }

  .mobile-profile-name {
    font-size: 16px;
    font-weight: 600;
    color: var(--sidebar-text);
    margin-bottom: 2px;
  }

  .mobile-profile-role {
    font-size: 13px;
    color: var(--sidebar-text-secondary);
  }

  .mobile-menu-divider {
    height: 1px;
    background: var(--sidebar-border);
    margin: 12px 0;
  }

  .content { 
    margin-left: 0 !important;
    max-width: 100vw !important;
    padding-bottom: calc(var(--mobile-bottom-nav-h) + 20px) !important;
  }

  .backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1028;
    backdrop-filter: blur(4px);
  }

  /* Hide desktop sidebar on mobile */
  .sidebar-pro {
    display: none;
  }
}

/* ==================== REDUCED MOTION ==================== */
@media (prefers-reduced-motion: reduce) {
  .nav-button,
  .submenu-panel,
  .submenu-content,
  .content,
  .sidebar-pro,
  .rightpanel {
    transition: none !important;
  }
}

/* ==================== COMMAND PALETTE ==================== */
.command-palette-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 2000;
  animation: fadeIn 0.15s ease;
}

.command-palette {
  position: fixed;
  top: 15vh;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 640px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  z-index: 2001;
  overflow: hidden;
  animation: slideDown 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideDown {
  from { 
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to { 
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.command-palette-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.command-palette-search-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.command-palette-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: #111827;
  background: transparent;
}

.command-palette-input::placeholder {
  color: #9ca3af;
}

.command-palette-kbd {
  padding: 4px 8px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  color: #6b7280;
  font-family: monospace;
  font-weight: 600;
}

.command-palette-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;
}

.command-palette-content::-webkit-scrollbar { width: 8px; }
.command-palette-content::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 999px;
}
.command-palette-content::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.command-palette-empty {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.command-palette-group {
  margin-bottom: 12px;
}

.command-palette-group-label {
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.05em;
}

.command-palette-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s ease;
}

.command-palette-item:hover,
.command-palette-item.selected {
  background: #f3f4f6;
  transform: translateX(2px);
}

.command-palette-item:active {
  transform: scale(0.99);
}

.command-palette-item-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.command-palette-item-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.command-palette-item-content {
  flex: 1;
  min-width: 0;
}

.command-palette-item-label {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  margin-bottom: 2px;
}

.command-palette-item-description {
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.command-palette-item-arrow {
  color: #9ca3af;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.command-palette-item:hover .command-palette-item-arrow,
.command-palette-item.selected .command-palette-item-arrow {
  opacity: 1;
}

/* Command palette locked items */
.command-palette-item--locked {
  opacity: 0.5;
  cursor: not-allowed;
}
.command-palette-item--locked:hover,
.command-palette-item--locked.selected {
  opacity: 0.65;
}
.pro-badge-cmd {
  display: inline-flex;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: #fff;
  padding: 1px 5px;
  border-radius: 4px;
  margin-left: 8px;
  vertical-align: middle;
  text-transform: uppercase;
}

.command-palette-footer {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 12px;
  color: #6b7280;
  background: transparent;
}

.command-palette-footer-item {
  display: flex;
  align-items: center;
  gap: 6px;
  display: none;
}

.command-palette-footer kbd {
  padding: 2px 6px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  font-size: 11px;
  font-family: monospace;
  font-weight: 600;
}

/* ==================== BREADCRUMBS ==================== */
.breadcrumbs {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 12px 24px;
  position: sticky;
  top: var(--topbar-h);
  z-index: 100;
}

.breadcrumbs-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.breadcrumbs-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.breadcrumbs-link,
.breadcrumbs-text {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #6b7280;
  text-decoration: none;
  transition: color 0.15s ease, transform 0.15s ease;
}

.breadcrumbs-link:hover {
  color: #3b82f6;
  transform: translateY(-1px);
}

.dark-mode .breadcrumbs-link:hover {
  color: #60a5fa;
}

.breadcrumbs-link:active {
  transform: translateY(0);
}

.breadcrumbs-link:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
  border-radius: 4px;
}

.breadcrumbs-current {
  color: #111827;
  font-weight: 500;
}

.breadcrumbs-icon {
  flex-shrink: 0;
}

.breadcrumbs-separator {
  color: #d1d5db;
  flex-shrink: 0;
}

/* ==================== ENHANCED TOOLTIP ==================== */
.enhanced-tooltip {
  position: fixed;
  z-index: 10000;
  pointer-events: none;
  animation: tooltipFadeIn 0.15s ease;
}

@keyframes tooltipFadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.enhanced-tooltip-content {
  background: #1f2937;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 280px;
}

.enhanced-tooltip--rich .enhanced-tooltip-content {
  padding: 10px 14px;
}

.enhanced-tooltip-simple {
  white-space: nowrap;
}

.enhanced-tooltip-rich {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.enhanced-tooltip-title {
  font-weight: 600;
  font-size: 13px;
}

.enhanced-tooltip-description {
  font-size: 12px;
  color: #d1d5db;
  line-height: 1.5;
}

.enhanced-tooltip-shortcut {
  display: flex;
  gap: 4px;
  margin-top: 4px;
}

.enhanced-tooltip-kbd {
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 3px;
  font-size: 11px;
  font-family: monospace;
  font-weight: 600;
}

.enhanced-tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
}

.enhanced-tooltip-arrow--top {
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px 6px 0 6px;
  border-color: #1f2937 transparent transparent transparent;
}

.enhanced-tooltip-arrow--bottom {
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 0 6px 6px 6px;
  border-color: transparent transparent #1f2937 transparent;
}

.enhanced-tooltip-arrow--left {
  right: -6px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 6px 0 6px 6px;
  border-color: transparent transparent transparent #1f2937;
}

.enhanced-tooltip-arrow--right {
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 6px 6px 6px 0;
  border-color: transparent #1f2937 transparent transparent;
}

/* ==================== TOAST NOTIFICATIONS ==================== */
.toast-container {
  position: fixed;
  top: calc(var(--topbar-h) + 20px);
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
}

.toast-item {
  pointer-events: all;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 320px;
  max-width: 420px;
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px var(--toast-border);
  animation: toastSlideIn 0.3s cubic-bezier(0.21, 1.02, 0.73, 1);
  transition: all 0.2s ease;
}

.toast-item:hover {
  transform: translateX(-4px) scale(1.02);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2), 0 0 0 1px var(--toast-border);
}

@keyframes toastSlideIn {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  margin-bottom: 2px;
}

.toast-message {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

.toast-close {
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-close:hover {
  background: #f3f4f6;
  color: #111827;
  transform: scale(1.1);
}

.toast-close:active {
  transform: scale(0.95);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .toast-container {
    right: 12px;
    left: 12px;
    top: calc(var(--topbar-h) + 12px);
  }
  
  .toast-item {
    min-width: 0;
    max-width: 100%;
  }
}

/* ==================== DARK MODE TOGGLE ==================== */
.dark-mode-toggle {
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  border-radius: 20px;
  padding: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.dark-mode-toggle:hover {
  background: var(--topbar-btn-hover);
  transform: translateY(-1px);
}

.dark-mode-toggle:active {
  transform: translateY(0);
}

.dark-mode-toggle:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.dark-mode-toggle-track {
  width: 44px;
  height: 22px;
  border-radius: 11px;
  background: rgba(0, 0, 0, 0.1);
  position: relative;
  transition: background 0.3s ease;
}

.dark-mode-toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fbbf24;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.dark-mode-icon {
  color: #f59e0b;
}

/* Dark mode styles */
.dark-mode .dark-mode-toggle-track {
  background: rgba(59, 130, 246, 0.3);
}

.dark-mode .dark-mode-toggle-thumb {
  left: 24px;
  background: #3b82f6;
}

.dark-mode .dark-mode-icon {
  color: #fff;
}
  `;
  if (!style) {
    style = document.createElement("style");
    style.id = "app-shell-styles";
    document.head.appendChild(style);
  }
  style.textContent = css;
};


/** Helpers for Nightscan logic (ported from your legacy header) */
// The machine stores NightScanTimeStampEnd as "YYYYMMDDHHMMSS" in Spain
// wall-clock time (Europe/Madrid), with no timezone info. To make the AppShell
// behave identically regardless of the browser's timezone we:
//   1. Build the Date using Date.UTC(...) so the UTC components of the Date
//      match the Spain wall-clock components of the string (getUTC* returns
//      Spain hours/minutes/year).
//   2. Compute "today / yesterday" in Spain via Intl to compare calendar days.
const SPAIN_TZ = "Europe/Madrid";
const VACWAY_CUSTOMER_ID = "620d1664db950220fabc0a41";
const NIGHTSCAN_EXCLUDED_SERIALS = ["SHOWROOM-I", "SHOWROOM-O", "VLK00000", "VLK00001"];

const getSpainDateParts = (date = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: SPAIN_TZ,
      year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(date);
    const pick = (t) => Number(parts.find(p => p.type === t)?.value);
    return { y: pick("year"), m: pick("month"), d: pick("day") };
  } catch (e) {
    // Fallback to browser local if Intl/timezone is unavailable
    return { y: date.getFullYear(), m: date.getMonth() + 1, d: date.getDate() };
  }
};

const formatNightscanDate = (timestamp) => {
  if (!timestamp || typeof timestamp !== "string" || timestamp.length < 14) {
    return new Date(NaN);
  }
  const year = Number(timestamp.substring(0, 4));
  const month = Number(timestamp.substring(4, 6)) - 1;
  const day = Number(timestamp.substring(6, 8));
  const hour = Number(timestamp.substring(8, 10));
  const minute = Number(timestamp.substring(10, 12));
  const second = Number(timestamp.substring(12, 14));
  if ([year, month, day, hour, minute, second].some(Number.isNaN)) {
    return new Date(NaN);
  }
  // Build via UTC so getUTC* components equal the Spain wall-clock components.
  return new Date(Date.UTC(year, month, day, hour, minute, second));
};

const isNightscanInSleepPeriod = (nightscanDate, sleepStart, sleepEnd) => {

  if (
    !nightscanDate ||
    !(nightscanDate instanceof Date) ||
    isNaN(nightscanDate)
  ) {
    return false;
  }

  // nightscanDate was built via Date.UTC(...) from the Spain wall-clock string,
  // so its UTC components ARE the Spain wall-clock components.
  const nsY = nightscanDate.getUTCFullYear();
  const nsM = nightscanDate.getUTCMonth() + 1;
  const nsD = nightscanDate.getUTCDate();
  const nsH = nightscanDate.getUTCHours();
  const nsMin = nightscanDate.getUTCMinutes();

  // Compute "today" and "yesterday" in Spain TZ so browser TZ doesn't matter.
  const todaySpain = getSpainDateParts(new Date());
  const todayUtcMs = Date.UTC(todaySpain.y, todaySpain.m - 1, todaySpain.d);
  const yestUtcMs = todayUtcMs - 24 * 60 * 60 * 1000;
  const yDate = new Date(yestUtcMs);
  const yesterdaySpain = {
    y: yDate.getUTCFullYear(),
    m: yDate.getUTCMonth() + 1,
    d: yDate.getUTCDate(),
  };

  const isToday =
    nsY === todaySpain.y && nsM === todaySpain.m && nsD === todaySpain.d;
  const isYesterday =
    nsY === yesterdaySpain.y && nsM === yesterdaySpain.m && nsD === yesterdaySpain.d;

  if (!isToday && !isYesterday) return false;

  // Compare by time-of-day only. A sleep window is a daily schedule, so if
  // the nightscan happened today or yesterday and its Spain time falls in
  // the window (straight or wrapping past midnight), it's in sleep.
  const startHours = Math.floor((sleepStart ?? 0) / 100);
  const startMinutes = (sleepStart ?? 0) % 100;
  const endHours = Math.floor((sleepEnd ?? 0) / 100);
  const endMinutes = (sleepEnd ?? 0) % 100;

  const nsTotal = nsH * 60 + nsMin;
  const startTotal = startHours * 60 + startMinutes;
  const endTotal = endHours * 60 + endMinutes;

  // No sleep window configured
  if (startTotal === endTotal) return false;

  if (startTotal < endTotal) {
    // Same-day window, e.g. 01:00 - 05:00
    return nsTotal >= startTotal && nsTotal <= endTotal;
  }

  // Crosses midnight, e.g. 23:00 - 07:00
  return nsTotal >= startTotal || nsTotal <= endTotal;
};

const getMachineStatus = (status, signal) => {
  if (!status) return "UNKNOWN";
  const now = Date.now();
  const lastSignalTime = Date.parse(signal);
  const diffInSeconds = (now - lastSignalTime) / 1000;
  if (diffInSeconds >= 60 * 5) return "OFFLINE";

  const map = {
    OPERATIVE: "ONLINE",
    SLEEP: "SLEEP",
    NIGHT_SCAN: "NIGHTSCAN",
    MAINTENANCE: "MANTENIMIENTO",
    LOCKERS_FULL: "LOCKERS LLENOS",
    TICKET_EMPTY: "SIN TICKET",
    OPERATIVE_SLEEP: "SLEEP ONLINE",
    MAINTENANCE_SLEEP: "SLEEP MAINTENANCE",
    KERONG_OTA: "OTA KERONG",
    BOOTLOADER_OTA: "OTA BOOTLOADER",
    BLE_OFF: "POWER DOWN",
  };
  return map[status] || "UNKNOWN";
};

const getMachineStatusColor = (status, signal) => {
  const now = Date.now();
  const lastSignalTime = Date.parse(signal);
  const diffInSeconds = (now - lastSignalTime) / 1000;
  if (diffInSeconds >= 60 * 5) return "#727370"; // Offline gray

  const colors = {
    OPERATIVE: "#28a745",
    SLEEP: "#0066ff",
    NIGHT_SCAN: "#26c4f6",
    MAINTENANCE: "#ff7b25",
    LOCKERS_FULL: "#ff0000",
    TICKET_EMPTY: "#ff0000",
    OPERATIVE_SLEEP: "#28a745",
    MAINTENANCE_SLEEP: "#ff7b25",
    KERONG_OTA: "#000000",
    BOOTLOADER_OTA: "#000000",
    BLE_OFF: "#288BA8",
    "FULL QR": "#800080",
  };
  return colors[status] || "#727370";
};

/**
 * Top bar
 * - Desktop: translator + user + logout, plus NEW Nightscan toggle button (before language).
 * - Mobile: user/logout hidden, but Nightscan toggle remains visible.
 */
const PRO_FEATURES_LIST = [
  { icon: QrCode,         label: 'Códigos QR',             desc: 'Genera y gestiona códigos QR para apertura de lockers.',      feature: 'qrs' },
  { icon: CreditCard,     label: 'Venta de lockers',        desc: 'Punto de venta integrado para tu equipo.',                    feature: 'lockersale' },
  { icon: Tag,            label: 'Códigos de descuento',    desc: 'Crea y distribuye códigos de descuento para clientes.',       feature: 'discount-codes' },
  { icon: Ticket,         label: 'Códigos de canje',        desc: 'Sistema de canje de vales y bonos.',                          feature: 'redeemcodes' },
  { icon: Key,            label: 'Reservas',                desc: 'Gestión avanzada de reservas y pre-asignaciones.',            feature: 'rentals' },
  { icon: CalendarCheck2, label: 'Calendarios',             desc: 'Tarifas y disponibilidad por fechas.',                        feature: 'calendars' },
  { icon: Mail,           label: 'Plantillas de email',     desc: 'Personaliza los correos automáticos a tus clientes.',         feature: 'email-templates' },
  { icon: UserCog,        label: 'Partners',                desc: 'Gestión de acuerdos y partners comerciales.',                 feature: 'partners' },
  { icon: Newspaper,      label: 'Integraciones API',       desc: 'Conecta con sistemas externos mediante la API REST.',         feature: 'integraciones' },
  { icon: Smartphone,     label: 'Usuarios PDA',            desc: 'Control y gestión de usuarios operativos con terminales PDA.', feature: 'my-pda-users' },
  { icon: Calculator,     label: 'Usuarios Cash-Control',   desc: 'Gestión de usuarios del módulo de control de efectivo.',      feature: 'my-cash-users' },
  { icon: Users,          label: 'Usuarios Manager',        desc: 'Gestión de usuarios Manager con acceso completo de Licencia Pro.', feature: 'my-manager-users' },
];

const CustomerProfileModal = ({ isOpen, onClose, loading, data, error }) => {
  if (!isOpen) return null;

  // Extract creation date from MongoDB ObjectId (first 4 bytes = unix timestamp)
  const createdAt = data && data._id
    ? new Date(parseInt(data._id.substring(0, 8), 16) * 1000)
    : null;

  const yearsAsClient = createdAt
    ? Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24 * 365.25))
    : 0;

  const anniversaryConfig = yearsAsClient >= 5
    ? { emoji: '🏆', label: `¡${yearsAsClient} años con nosotros!`, bg: 'linear-gradient(135deg,#f59e0b,#d97706)', text: '#fff' }
    : yearsAsClient >= 3
    ? { emoji: '🥇', label: `¡${yearsAsClient} años con nosotros!`, bg: 'linear-gradient(135deg,#6366f1,#8b5cf6)', text: '#fff' }
    : yearsAsClient >= 2
    ? { emoji: '🥈', label: `¡${yearsAsClient} años con nosotros!`, bg: 'linear-gradient(135deg,#3b82f6,#06b6d4)', text: '#fff' }
    : yearsAsClient >= 1
    ? { emoji: '🥉', label: `¡${yearsAsClient} año con nosotros!`, bg: 'linear-gradient(135deg,#10b981,#059669)', text: '#fff' }
    : null;

  const Section = ({ title, icon: Icon, children }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Icon size={13} style={{ color: 'var(--sidebar-text-secondary, #9ca3af)' }} />
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--sidebar-text-secondary, #9ca3af)' }}>{title}</span>
      </div>
      <div style={{ borderRadius: 10, border: '1px solid var(--sidebar-border, #e5e7eb)', overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );

  const Row = ({ icon: Icon, label, value, mono }) => {
    if (!value) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 12px', borderBottom: '1px solid var(--sidebar-border, #f3f4f6)', background: 'var(--topbar-bg, #fff)' }}>
        {Icon && <Icon size={14} style={{ color: 'var(--sidebar-text-secondary, #9ca3af)', marginTop: 1, flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, color: 'var(--sidebar-text-secondary, #9ca3af)', fontWeight: 600, letterSpacing: '0.04em', marginBottom: 1 }}>{label}</div>
          <div style={{ fontSize: 13, color: 'var(--sidebar-text, #111827)', fontWeight: 500, wordBreak: 'break-word', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</div>
        </div>
      </div>
    );
  };

  const TypeBadge = ({ value }) => {
    if (!value) return null;
    const labels = {
      LICENCIA_PRO:    'Licencia Pro',
      LICENCIA_BASICA: 'Licencia Básica',
      GROUP:           'Grupo',
      CASH:            'Cash-Control',
      PDA:             'Operativo',
      MANAGER:         'Manager',
      GENERAL:         'General',
    };
    const colors = {
      LICENCIA_PRO:   { bg: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)', text: '#ffffff', border: '#f97316' },
      LICENCIA_BASICA:{ bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
      GROUP:          { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0' },
      CASH:           { bg: '#fdf4ff', text: '#6b21a8', border: '#e9d5ff' },
      PDA:            { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa' },
      MANAGER:        { bg: 'linear-gradient(135deg,#8b5cf6,#5b21b6)', text: '#ffffff', border: '#7c3aed' },
      GENERAL:        { bg: 'var(--sidebar-btn-hover,#f3f4f6)', text: 'var(--sidebar-text-secondary,#6b7280)', border: 'var(--sidebar-border,#e5e7eb)' },
    };
    const c = colors[value] || colors.GENERAL;
    const label = labels[value] || value;
    return (
      <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>{label}</span>
    );
  };

  const locationParts = [data && data.Address, data && data.City, data && data.Province, data && data.Country].filter(Boolean);
  const hasLocation = locationParts.length > 0;
  const hasContact = data && (data.Contact_person || data.Phone || data.Phone2 || data.Web);
  const hasFiscal  = data && (data.Social_name || data.Invoice_taxid || data.Invoice_address);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(4px)',
    }} onClick={onClose}>
      <div style={{
        background: 'var(--topbar-bg, #fff)', borderRadius: 16, width: '100%', maxWidth: 860,
        maxHeight: '90vh', overflowY: 'auto', padding: 'clamp(16px, 4vw, 32px)',
        boxShadow: '0 24px 60px rgba(0,0,0,.3)', color: 'var(--sidebar-text, #1f2937)',
      }} onClick={e => e.stopPropagation()}>
        <style>{`
          @media (max-width: 600px) {
            .cpm-sections-grid { grid-template-columns: 1fr !important; }
            .cpm-hero { flex-direction: column; align-items: flex-start !important; }
          }
        `}</style>

        {/* ── Header ─────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserIcon size={20} style={{ color: 'var(--sidebar-text-secondary, #6b7280)' }} />
            <span style={{ fontSize: 16, fontWeight: 700 }}>Mi cuenta</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ffffff', fontSize: 20, lineHeight: 1, padding: '2px 4px', borderRadius: 6 }}>✕</button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--sidebar-text-secondary, #6b7280)' }}>Cargando...</div>
        )}
        {error && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#ef4444', fontSize: 13 }}>No se pudo cargar la información de la cuenta.</div>
        )}

        {data && !loading && (
          <div>
            {/* ── Anniversary banner ─────────────────────── */}
            {anniversaryConfig && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 10, background: anniversaryConfig.bg, color: anniversaryConfig.text, marginBottom: 14, fontWeight: 700, fontSize: 14 }}>
                <span style={{ fontSize: 22 }}>{anniversaryConfig.emoji}</span>
                <div>
                  <div>{anniversaryConfig.label}</div>
                  <div style={{ fontWeight: 400, fontSize: 11, opacity: 0.85 }}>
                    Cliente desde {createdAt.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                </div>
              </div>
            )}
            {!anniversaryConfig && createdAt && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 8, background: 'var(--sidebar-btn-hover, rgba(0,0,0,.04))', marginBottom: 14, fontSize: 12, color: 'var(--sidebar-text-secondary, #6b7280)' }}>
                <span>📅</span>
                <span>Cliente desde {createdAt.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            )}

            {/* ── Hero card ──────────────────────────────── */}
            <div className="cpm-hero" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, padding: '16px', borderRadius: 14, background: 'linear-gradient(135deg, rgba(59,130,246,.08) 0%, rgba(99,102,241,.08) 100%)', border: '1px solid rgba(99,102,241,.15)' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 12px rgba(99,102,241,.35)' }}>
                <span style={{ color: '#fff', fontWeight: 800, fontSize: 22 }}>{(data.Fullname || '?')[0].toUpperCase()}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 3 }}>{data.Fullname}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--sidebar-text-secondary, #6b7280)', marginBottom: 8 }}>
                  <Mail size={12} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.Email}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <TypeBadge value={data.Type} />
                </div>
              </div>
            </div>

            {/* ── Sections grid ──────────────────────────── */}
            <div className="cpm-sections-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0 16px', alignItems: 'start' }}>
              {/* Contact */}
              {hasContact && (
                <Section title="Contacto" icon={Phone}>
                  <Row icon={UserIcon}  label="Persona de contacto" value={data.Contact_person} />
                  <Row icon={Phone}     label="Teléfono"            value={data.Phone} />
                  <Row icon={Phone}     label="Teléfono secundario" value={data.Phone2} />
                  <Row icon={Globe}     label="Web"                 value={data.Web} />
                </Section>
              )}

              {/* Location */}
              {hasLocation && (
                <Section title="Ubicación" icon={MapPin}>
                  <Row icon={MapPin}    label="Dirección"           value={data.Address} />
                  <Row icon={MapPin}    label="Ciudad"              value={data.City} />
                  <Row icon={MapPin}    label="Provincia"           value={data.Province} />
                  <Row icon={Globe}     label="País"                value={data.Country} />
                  <Row icon={Tag}       label="Código postal"       value={data.PostalCode} />
                </Section>
              )}

              {/* Fiscal */}
              {hasFiscal && (
                <Section title="Facturación" icon={Building2}>
                  <Row icon={Building2}   label="Razón social"      value={data.Social_name} />
                  <Row icon={ShieldCheck} label="NIF / CIF"         value={data.Invoice_taxid} mono />
                  <Row icon={MapPin}      label="Dirección fiscal"  value={data.Invoice_address} />
                </Section>
              )}
            </div>

            {/* ── Footer ─────────────────────────────────── */}
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
              <button style={{ padding: '9px 24px', borderRadius: 40, border: 'none', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#ffffff', boxShadow: '0 2px 8px rgba(99,102,241,.3)' }} onClick={onClose}>Cerrar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ProUpgradeModal = ({ isOpen, onClose, onRequest, loading, sent, error, role, type, category }) => {
  if (!isOpen) return null;
  const visibleFeatures = PRO_FEATURES_LIST.filter(f => isFeatureLocked(role, type, category, f.feature));
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(4px)',
    }} onClick={onClose}>
      <div style={{
        background: 'var(--topbar-bg, #fff)', borderRadius: 16, width: '100%', maxWidth: 680,
        maxHeight: '85vh', overflowY: 'auto', padding: '28px 32px',
        boxShadow: '0 24px 60px rgba(0,0,0,.3)', color: 'var(--sidebar-text, #1f2937)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Star size={22} style={{ color: '#f59e0b' }} />
            <span style={{ fontSize: 18, fontWeight: 700 }}>Desbloquea VACWAYlockers <span style={{ color: '#f59e0b' }}>PRO</span></span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sidebar-text, #6b7280)', fontSize: 20, lineHeight: 1 }}>✕</button>
        </div>
        {sent ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>¡Solicitud enviada!</div>
            <div style={{ color: 'var(--sidebar-text-secondary, #6b7280)', fontSize: 14 }}>Nuestro equipo se pondrá en contacto contigo en breve para gestionar la actualización a PRO.</div>
            <button style={{ marginTop: 24, padding: '10px 28px', borderRadius: 40, border: 'none', background: '#f59e0b', color: '#fff', fontWeight: 700, cursor: 'pointer' }} onClick={onClose}>Cerrar</button>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 13, color: 'var(--sidebar-text-secondary, #6b7280)', marginBottom: 20 }}>Con la licencia <strong>PRO</strong> desbloqueas todas las capacidades de la plataforma:</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', marginBottom: 28 }}>
              {visibleFeatures.map(f => (
                <div key={f.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ marginTop: 2, color: '#f59e0b', flexShrink: 0 }}><f.icon size={16} /></div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{f.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--sidebar-text-secondary, #9ca3af)' }}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexDirection: 'column', alignItems: 'flex-end' }}>
              {error && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 4 }}>No se pudo enviar la solicitud. Inténtalo de nuevo más tarde.</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={{ padding: '10px 20px', borderRadius: 40, border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', fontSize: 13 }} className={'text-white'} onClick={onClose}>Cerrar</button>
                <button
                  style={{ padding: '10px 24px', borderRadius: 40, border: 'none', background: loading ? '#d97706' : '#f59e0b', color: '#fff', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: loading ? 0.8 : 1 }}
                  onClick={onRequest}
                  disabled={loading}
                >
                  <Star size={14} />{loading ? 'Enviando...' : 'Solicitar actualización a PRO'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const TopBar = ({
  fullname,
  onLogout,
  isMobile,
  mobileOpen,
  setMobileOpen,
  role,
  type,
  category,
  nightscanCount,
  nightscanLoading,
  openTicketsCount,
  openIncidencesCount,
  onToggleNightscan,
  onToggleSidebarMobile,
  onOpenCommandPalette,
  onUserClick,
  push,
  showProModal,
  onOpenProModal,
  onCloseProModal,
  onProRequest,
  proUpgradeLoading,
  proUpgradeSent,
  proUpgradeError,
}) => {
  const [showChipMenu, setShowChipMenu] = useState(false);
  const chipCloseTimer = useRef(null);
  const showAdminNightscan = canAccess(role, type, category, 'nightscan');

  const PLATFORMS = [
    { label: 'TICKETING',   href: 'https://ticketing.vacway.es', color: '#f97316' },
    { label: 'FASTPASS',    href: 'https://fastpass.vacway.es',  color: '#8b5cf6' },
    { label: 'WATERPROOF',  href: 'https://one.vacway.es',       color: '#84cc16' },
    ...(role === 'ADMIN' ? [
      { label: 'CLOCK',     href: 'https://clock.vacway.es',     color: '#06b6d4' },
      { label: 'REPORTING', href: 'https://reporting.vacway.es', color: '#ec4899' },
    ] : []),
  ];
  
  // Show tickets indicator for MASTER and SAT1
  const showTicketsIndicator = role === 'ADMIN' && (type === 'MASTER' || type === 'SAT1');
  // Show incidences indicator for MASTER and SAT2
  const showIncidencesIndicator = role === 'ADMIN' && (type === 'MASTER' || type === 'SAT2');

  return (
    <div className="topbar">
      <div className="topbar__inner">
        <div className="topbar__left">
          <Link to="/" className="topbar__brand mr-3" aria-label="Home">
            <img src="/images/logo_white.png" alt="Logo" className="logo-dark" />
            <img src="/images/logo_blue.png" alt="Logo" className="logo-light" />
          </Link>
          {/* ── Platform switcher ── */}
          <div
            style={{ position: 'relative' }}
            onPointerEnter={(e) => { if (e.pointerType === 'mouse') { clearTimeout(chipCloseTimer.current); setShowChipMenu(true); } }}
            onPointerLeave={(e) => { if (e.pointerType === 'mouse') { chipCloseTimer.current = setTimeout(() => setShowChipMenu(false), 120); } }}
          >
            <button
              className="psw-chip"
              onClick={() => setShowChipMenu(v => !v)}
              aria-label="Cambiar plataforma"
            >
              <span className="psw-chip-dot" />
              <span>LOCKERS</span>
              <ChevronDown size={13} className={`psw-chip-chevron${showChipMenu ? ' psw-chip-chevron--open' : ''}`} />
            </button>
            {showChipMenu && (
              <div className="psw-chip-panel">
                {PLATFORMS.map(p => (
                  <a key={p.label} href={p.href} className="psw-chip-card">
                    <span className="psw-chip-card-accent" style={{ background: p.color }} />
                    <span className="psw-chip-card-label">{p.label}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="topbar__right">
          {/* Command Palette trigger */}
          <EnhancedTooltip
            content="Búsqueda rápida"
            description="Buscar y navegar rápidamente"
            shortcut={['⌘K']}
            position="bottom"
          >
            <button
              className="btn-ghost"
              onClick={onOpenCommandPalette}
              title="Búsqueda rápida"
              aria-label="Abrir búsqueda rápida (Cmd+K)"
              style={{ marginRight: 8 }}
            >
              <Search size={18} />
              {!isMobile && <span style={{ marginLeft: 6 }}>Buscar</span>}
            </button>
          </EnhancedTooltip>

          {/* Nightscan toggle (before language) */}
          {showAdminNightscan && (
            <EnhancedTooltip
              content="Incidencias Nightscan"
              description="Ver máquinas con incidencias de escaneo nocturno"
              position="bottom"
            >
              <button
                className="nightscan-btn"
                onClick={onToggleNightscan}
                title="Incidencias Nightscan"
                aria-label="Abrir/cerrar Nightscan"
              >
                <Info size={18} />
                {!isMobile && <span>Nightscan</span>}
                {nightscanLoading
                  ? <span className="nightscan-badge nightscan-badge--loading" aria-label="Cargando"><span className="nightscan-spinner" /></span>
                  : (!!nightscanCount && <span className="nightscan-badge">{nightscanCount}</span>)
                }
              </button>
            </EnhancedTooltip>
          )}

          {/* Tickets indicator - MASTER and SAT1 only */}
          {showTicketsIndicator && (
            <EnhancedTooltip
              content="Tickets Abiertos"
              description="Ver tickets de soporte abiertos"
              position="bottom"
            >
              <button
                className="tickets-indicator-btn"
                onClick={() => push('/supporttickets')}
                title="Tickets Abiertos"
                aria-label="Ver tickets abiertos"
              >
                <Ticket size={18} />
                {!isMobile && <span>Tickets</span>}
                {openTicketsCount > 0 && <span className="indicator-badge">{openTicketsCount}</span>}
              </button>
            </EnhancedTooltip>
          )}

          {/* Incidences indicator - MASTER and SAT2 only */}
          {showIncidencesIndicator && (
            <EnhancedTooltip
              content="Incidencias Abiertas"
              description="Ver incidencias abiertas"
              position="bottom"
            >
              <button
                className="incidences-indicator-btn"
                onClick={() => push('/incidences')}
                title="Incidencias Abiertas"
                aria-label="Ver incidencias abiertas"
              >
                <AlertTriangle size={18} />
                {!isMobile && <span>Incidencias</span>}
                {openIncidencesCount > 0 && <span className="indicator-badge">{openIncidencesCount}</span>}
              </button>
            </EnhancedTooltip>
          )}

          <div className="mr-2 d-none d-md-flex"><GoogleTranslate /></div>

          {/* Dark Mode Toggle */}
          {!isMobile && (
            <div style={{ marginRight: 8 }}>
              <EnhancedTooltip
                content="Tema"
                description="Cambiar entre modo claro y oscuro"
                position="bottom"
              >
                <DarkModeToggle />
              </EnhancedTooltip>
            </div>
          )}

          {/* Desktop-only actions */}
          {!isMobile && (
            <>
              <EnhancedTooltip content={fullname} position="bottom">
                <button type="button" className="btn-ghost user-btn" title={fullname} onClick={onUserClick}>
                  {role === 'ADMIN' ? (
                    <img
                      src={`https://vacway-images.s3.eu-central-1.amazonaws.com/${fullname}.png`}
                      alt={fullname}
                      style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', marginRight: 6 }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://vacway-images.s3.eu-central-1.amazonaws.com/default.png";
                      }}
                    />
                  ) : (
                    <UserIcon size={18} />
                  )}
                  <span className="d-none d-sm-inline">{fullname}</span>
                  {type === 'LICENCIA_PRO' && (
                    <span style={{ marginLeft: 7, padding: '2px 7px', borderRadius: 20, background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1, verticalAlign: 'middle', lineHeight: 1.5 }}>PRO</span>
                  )}
                  {type === 'LICENCIA_BASICA' && (
                    <span style={{ marginLeft: 7, padding: '2px 7px', borderRadius: 20, background: 'rgba(100,116,139,.25)', color: 'var(--topbar-text,#fff)', fontSize: 10, fontWeight: 700, letterSpacing: 1, verticalAlign: 'middle', lineHeight: 1.5 }}>BASIC</span>
                  )}
                  {type === 'PDA' && (
                    <span style={{ marginLeft: 7, padding: '2px 7px', borderRadius: 20, background: 'linear-gradient(90deg,#3b82f6,#1d4ed8)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1, verticalAlign: 'middle', lineHeight: 1.5 }}>OPERATIVO</span>
                  )}
                  {type === 'CASH' && (
                    <span style={{ marginLeft: 7, padding: '2px 7px', borderRadius: 20, background: 'linear-gradient(90deg,#10b981,#065f46)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1, verticalAlign: 'middle', lineHeight: 1.5 }}>CASH-CONTROL</span>
                  )}
                  {type === 'MANAGER' && (
                    <span style={{ marginLeft: 7, padding: '2px 7px', borderRadius: 20, background: 'linear-gradient(90deg,#8b5cf6,#5b21b6)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1, verticalAlign: 'middle', lineHeight: 1.5 }}>MANAGER</span>
                  )}
                  {type === 'GROUP' && (
                    <span style={{ marginLeft: 7, padding: '2px 7px', borderRadius: 20, background: 'linear-gradient(90deg,#f59e0b,#b45309)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1, verticalAlign: 'middle', lineHeight: 1.5 }}>GROUP</span>
                  )}
                </button>
              </EnhancedTooltip>
              {type === 'LICENCIA_BASICA' && (
                <EnhancedTooltip content="Actualizar a PRO" position="bottom">
                  <button
                    className="btn-ghost"
                    style={{ marginLeft: 6, color: '#f59e0b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}
                    onClick={() => push('/planes')}
                  >
                    <Star size={15} />
                    <span className="d-none d-sm-inline">PRO</span>
                  </button>
                </EnhancedTooltip>
              )}
              <EnhancedTooltip content="Cerrar sesión" position="bottom">
                <button className="btn-ghost logout-btn" onClick={onLogout} style={{ marginLeft: 8 }}>
                  <LogOut size={18} />
                  <span className="d-none d-sm-inline">Salir</span>
                </button>
              </EnhancedTooltip>
              <ProUpgradeModal isOpen={showProModal} onClose={onCloseProModal} onRequest={onProRequest} loading={proUpgradeLoading} sent={proUpgradeSent} error={proUpgradeError} role={role} type={type} category={category} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Sidebar items
 */
const Group = ({ label, children }) => (
  <div className="nav-group">
    <div className="nav-group__label">{label}</div>
    {children}
  </div>
);

const Collapsible = ({ icon: Icon, label, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="nav-item">
      <button className="nav-button" onClick={() => setOpen(!open)}>
        <span className="nav-button__icon"><Icon size={18} /></span>
        <span className="nav-button__label">{label}</span>
        {open ? <ChevronDown size={16} className="nav-button__chev" /> : <ChevronRight size={16} className="nav-button__chev" />}
      </button>
      {open && <div className="subnav">{children}</div>}
    </div>
  );
};

/**
 * Sidebar (desktop collapsible + mobile drawer)
 */
const Sidebar = ({ role, type, category, onNavigate, activeMenu, setActiveMenu, showProModal, onOpenProModal, onCloseProModal, onProRequest, proUpgradeLoading, proUpgradeSent, proUpgradeError }) => {
  const toggleMenu = (menu) => setActiveMenu(prev => (prev === menu ? null : menu));

  // Pro-lock toast state
  const [proToast, setProToast] = React.useState(false);
  const proToastTimer = React.useRef(null);
  const showProToast = () => {
    setProToast(true);
    if (proToastTimer.current) clearTimeout(proToastTimer.current);
    proToastTimer.current = setTimeout(() => setProToast(false), 3500);
  };

  // Check if user has access to any feature in each category
  const hasAcciones = hasAccessToAny(role, type, category, ['montarto', 'lockersale', 'pin', 'supportticketadd']);
  const hasCanales = hasAccessToAny(role, type, category, ['machines', 'qrs', 'pdas']);
  const hasGestion = hasAccessToAny(role, type, category, ['lockersale', 'pin', 'redeemcodes', 'wristband-redeemcodes', 'rentals', 'calendars']);
  const hasReporte = hasAccessToAny(role, type, category, ['pro-dashboard', 'operational-dashboard','occupancy-dashboard','sales', 'tiempos', 'ocupation', 'objectives', 'pinrecovers', 'localizacion', 'withdrawals', 'api', 'dashboard-rental', 'batteries']);
  const hasOperativa = hasAccessToAny(role, type, category, ['customers', 'contracts', 'audit', 'tpvs', 'modules', 'matrixs', 'lowbluetooth', 'lockersqty', 'openings', 'updates', 'software-versions', 'rental-sanity']);
  const hasSAT = hasAccessToAny(role, type, category, ['supporttickets', 'incidences', 'faults', 'replacements', 'slas']);
  const hasBaqueira = hasAccessToAny(role, type, category, ['operators', 'rules', 'articles', 'arqueosbaqueira', 'montarto', 'ocupationaccumulated', 'ocupationdaily']);
  const hasSoporte = hasAccessToAny(role, type, category, ['supportticketadd', 'faqs', 'manuals', 'support-chat', 'integraciones']);
  const hasAdmin = hasAccessToAny(role, type, category, ['users', 'gameranking', 'duplicated-sales']);
  // Items del bloque "Extras" del menú Administración (todo menos Administradores). Si el usuario
  // no puede ver ninguno, se oculta también la etiqueta EXTRAS para no dejarla huérfana.
  const hasAdminExtras = hasAccessToAny(role, type, category, ['gameranking', 'duplicated-sales']);
  const hasComercial = hasAccessToAny(role, type, category, ['sales-modes', 'ar-viewer']);
  const hasUsuarios = hasAccessToAny(role, type, category, ['my-cash-users', 'my-pda-users', 'my-manager-users']);

  const NavLinkItem = ({ to, icon: Icon, children }) => {
    const feature = to.replace(/^\//, '');
    const locked = isFeatureLocked(role, type, category, feature);

    if (locked) {
      return (
        <div
          className="nav-button nav-button--locked"
          onClick={(e) => { e.preventDefault(); onOpenProModal(); }}
          role="button"
          tabIndex={0}
        >
          {Icon && <Icon size={16} />}
          <span>{children}</span>
          <span className="pro-badge-inline">PRO</span>
          <Lock size={12} className="lock-icon-inline" />
        </div>
      );
    }

    return (
      <RRNavLink
        to={to}
        className="nav-button"
        onClick={() => {
          setActiveMenu(null);
          onNavigate();
        }}
      >
        {Icon && <Icon size={16} />}
        <span>{children}</span>
      </RRNavLink>
    );
  };

  return (
    <div className="sidebar-pro">
      {/* Left icons column */}
      <div className="sidebar-icons">
        {hasCanales && (
          <EnhancedTooltip
            content="Canales"
            description="Gestión de canales de venta"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "canales" ? "active" : ""}`}
              onClick={() => toggleMenu("canales")}
              title="Canales"
              aria-label="Menú de canales"
            >
              <Monitor size={20} />
              <span className="icon-label">Canales</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasGestion && (
          <EnhancedTooltip
            content="Gestión"
            description="Gestión de ventas, reservas y calendarios"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "gestion" ? "active" : ""}`}
              onClick={() => toggleMenu("gestion")}
              title="Gestión"
              aria-label="Menú de gestión"
            >
              <CalendarCheck2 size={20} />
              <span className="icon-label">Gestión</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasReporte && (
          <EnhancedTooltip
            content="Reporte"
            description="Reportes y análisis de datos"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "reporte" ? "active" : ""}`}
              onClick={() => toggleMenu("reporte")}
              title="Reporte"
              aria-label="Menú de reportes"
            >
              <BarChart2 size={20} />
              <span className="icon-label">Reporte</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasOperativa && (
          <EnhancedTooltip
            content="Operativa"
            description="Gestión operativa del sistema"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "operativa" ? "active" : ""}`}
              onClick={() => toggleMenu("operativa")}
              title="Operativa"
              aria-label="Menú de operativa"
            >
              <SlidersHorizontal size={20} />
              <span className="icon-label">Operativa</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasSAT && (
          <EnhancedTooltip
            content="SAT"
            description="Servicio de atención técnica"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "sat" ? "active" : ""}`}
              onClick={() => toggleMenu("sat")}
              title="SAT"
              aria-label="Menú de SAT"
            >
              <Headset size={20} />
              <span className="icon-label">SAT</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasBaqueira && (
          <EnhancedTooltip
            content="Baqueira"
            description="Operaciones específicas de Baqueira"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "baqueira" ? "active" : ""}`}
              onClick={() => toggleMenu("baqueira")}
              title="Baqueira"
              aria-label="Menú de Baqueira"
            >
              <Mountain size={20} />
              <span className="icon-label">Baqueira</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasUsuarios && (
          <EnhancedTooltip
            content="Usuarios"
            description="Gestión de usuarios Operativo y Cash-Control"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "usuarios" ? "active" : ""}`}
              onClick={() => toggleMenu("usuarios")}
              title="Usuarios"
              aria-label="Menú de usuarios"
            >
              <Users size={20} />
              <span className="icon-label">Usuarios</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasSoporte && (
          <EnhancedTooltip
            content="Soporte"
            description="Recursos de ayuda y soporte"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "soporte" ? "active" : ""}`}
              onClick={() => toggleMenu("soporte")}
              title="Soporte"
              aria-label="Menú de soporte"
            >
              <HelpCircle size={20} />
              <span className="icon-label">Soporte</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasComercial && (
          <EnhancedTooltip
            content="Comercial"
            description="Herramientas y documentación comercial"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "comercial" ? "active" : ""}`}
              onClick={() => toggleMenu("comercial")}
              title="Comercial"
              aria-label="Menú comercial"
            >
              <Briefcase size={20} />
              <span className="icon-label">Comercial</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasAdmin && (
          <EnhancedTooltip
            content="Administración"
            description="Configuración y administración del sistema"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "admin" ? "active" : ""}`}
              onClick={() => toggleMenu("admin")}
              title="Administración"
              aria-label="Menú de administración"
            >
              <Crown size={20} />
              <span className="icon-label">Admin</span>
            </button>
          </EnhancedTooltip>
        )}
        {/* PRO upgrade button below last category – only for BASIC users */}
        {type === 'LICENCIA_BASICA' && (
          <EnhancedTooltip content="Actualizar a PRO" description="Mejora tu licencia a PRO" position="right" delay={300}>
            <button
              className="icon-btn icon-btn--pro"
              onClick={() => onNavigate('/planes')}
              title="Actualizar a PRO"
              aria-label="Actualizar a licencia PRO"
            >
              <Star size={20} />
              <span className="icon-label">PRO</span>
            </button>
          </EnhancedTooltip>
        )}
      </div>

      {/* Right submenu panel */}
      <div className={`submenu-panel ${activeMenu ? "open" : ""}`}>
        {activeMenu === "canales" && (
          <div className="submenu-content">
            <div className="submenu-label">Canales de venta</div>
            {canAccess(role, type, category, 'machines') && (
              <NavLinkItem to="/machines" icon={Monitor}>{category === 'BAQUEIRA' ? 'Controladores' : 'Kioscos'}</NavLinkItem>
            )}
            {canAccess(role, type, category, 'qrs') && (
              <NavLinkItem to="/qrs" icon={QrCode}>QRs</NavLinkItem>
            )}
            {canAccess(role, type, category, 'pdas') && (
              <NavLinkItem to="/pdas" icon={Smartphone}>PDAs</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "gestion" && (
          <div className="submenu-content">
            <div className="submenu-label">Gestión</div>
            {canAccess(role, type, category, 'lockersale') && (
              <NavLinkItem to="/lockersale" icon={CreditCard}>Vender</NavLinkItem>
            )}
            {canAccess(role, type, category, 'pin') && (
              <NavLinkItem to="/pin" icon={Unlock}>Recuperar Pin</NavLinkItem>
            )}
            {canAccess(role, type, category, 'redeemcodes') && (
              <NavLinkItem to="/redeemcodes" icon={Ticket}>Códigos Canjeables</NavLinkItem>
            )}
            {canAccess(role, type, category, 'wristband-redeemcodes') && (
              <NavLinkItem to="/wristband-redeemcodes" icon={KeySquare}>Pulseras RFID</NavLinkItem>
            )}
            {canAccess(role, type, category, 'rentals') && (
              <NavLinkItem to="/rentals" icon={Key}>Reservas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'calendars') && (
              <NavLinkItem to="/calendars" icon={CalendarCheck2}>Calendarios</NavLinkItem>
            )}
            {canAccess(role, type, category, 'email-templates') && (
              <NavLinkItem to="/email-templates" icon={Mail}>Plantillas Email</NavLinkItem>
            )}
            {canAccess(role, type, category, 'partners') && (
              <NavLinkItem to="/partners" icon={UserCog}>Partners</NavLinkItem>
            )}
            {canAccess(role, type, category, 'discount-codes') && (
              <NavLinkItem to="/discount-codes" icon={Tag}>Códigos Descuento</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "reporte" && (
          <div className="submenu-content">
            <div className="submenu-label">Reporte</div>
            {canAccess(role, type, category, 'pro-dashboard') && (
              <NavLinkItem to="/pro-dashboard" icon={BarChart2}>Dashboard</NavLinkItem>
            )}
            {canAccess(role, type, category, 'dashboard-rental') && (
              <NavLinkItem to="/dashboard-rental" icon={CalendarCheck2}>Dashboard Reservas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'operational-dashboard') && (
              <NavLinkItem to="/operational-dashboard" icon={BarChart2}>Dashboard Operativo</NavLinkItem>
            )}
            {canAccess(role, type, category, 'occupancy-dashboard') && (
              <NavLinkItem to="/occupancy-dashboard" icon={PieChart}>Dashboard Ocupación</NavLinkItem>
            )}
            {canAccess(role, type, category, 'sales') && (
              <NavLinkItem to="/sales" icon={Filter}>Ventas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'tiempos') && (
              <NavLinkItem to="/tiempos" icon={Timer}>Tiempos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'localizacion') && (
              <NavLinkItem to="/localizacion" icon={Route}>Localización</NavLinkItem>
            )}
            {canAccess(role, type, category, 'objectives') && (
              <NavLinkItem to="/objectives" icon={Target}>Objetivos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'pinrecovers') && (
              <NavLinkItem to="/pinrecovers" icon={Lock}>Recuperación pines</NavLinkItem>
            )}
            {canAccess(role, type, category, 'withdrawals') && (
              <NavLinkItem to="/withdrawals" icon={Calculator}>Retiros</NavLinkItem>
            )}
            {canAccess(role, type, category, 'api') && (
              <NavLinkItem to="/api" icon={Code}>API</NavLinkItem>
            )}
            {canAccess(role, type, category, 'activity-dashboard') && (
              <NavLinkItem to="/activity-dashboard" icon={Users}>Actividad</NavLinkItem>
            )}
            {canAccess(role, type, category, 'batteries') && (
              <NavLinkItem to="/batteries" icon={BatteryFull}>Baterías</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "operativa" && (
          <div className="submenu-content">
            <div className="submenu-label">Operativa</div>
            {canAccess(role, type, category, 'customers') && (
              <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
            )}
            {canAccess(role, type, category, 'contracts') && (
              <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'audit') && (
              <NavLinkItem to="/audit" icon={Database}>Audit</NavLinkItem>
            )}
            {canAccess(role, type, category, 'tpvs') && (
              <NavLinkItem to="/tpvs" icon={Smartphone}>TPVs</NavLinkItem>
            )}
            {canAccess(role, type, category, 'modules') && (
              <NavLinkItem to="/modules" icon={LayoutGrid}>Módulos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'modules') && (
              <NavLinkItem to="/change-sn" icon={Replace}>Cambiar Locker</NavLinkItem>
            )}
            {canAccess(role, type, category, 'matrixs') && (
              <NavLinkItem to="/matrixs" icon={Download}>Firmware</NavLinkItem>
            )}
            {canAccess(role, type, category, 'lowbluetooth') && (
              <NavLinkItem to="/lowbluetooth" icon={Wifi}>Bluetooth</NavLinkItem>
            )}
            {canAccess(role, type, category, 'lockersqty') && (
              <NavLinkItem to="/lockersqty" icon={BarChart2}>Recurrencia</NavLinkItem>
            )}
            {canAccess(role, type, category, 'openings') && (
              <NavLinkItem to="/openings" icon={DoorOpen}>Aperturas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'updates') && (
              <NavLinkItem to="/updates" icon={RefreshCw}>Status Updates</NavLinkItem>
            )}
            {canAccess(role, type, category, 'software-versions') && (
              <NavLinkItem to="/software-versions" icon={Code}>Versiones SW</NavLinkItem>
            )}
            {canAccess(role, type, category, 'rental-sanity') && (
              <NavLinkItem to="/rental-sanity" icon={ClipboardCheck}>Rental Sanity</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "sat" && (
          <div className="submenu-content">
            <div className="submenu-label">SAT</div>
            {canAccess(role, type, category, 'supporttickets') && (
              <NavLinkItem to="/supporttickets" icon={Receipt}>Tickets</NavLinkItem>
            )}
            {canAccess(role, type, category, 'incidences') && (
              <NavLinkItem to="/incidences" icon={SlidersHorizontal}>Incidéncias</NavLinkItem>
            )}
            {canAccess(role, type, category, 'faults') && (
              <NavLinkItem to="/faults" icon={Info}>Averías</NavLinkItem>
            )}
            {canAccess(role, type, category, 'replacements') && (
              <NavLinkItem to="/replacements" icon={SlidersHorizontal}>Recambios</NavLinkItem>
            )}
            {canAccess(role, type, category, 'slas') && (
              <NavLinkItem to="/slas" icon={ShieldCheck}>SLA</NavLinkItem>
            )}
            {canAccess(role, type, category, 'bowler') && (
              <NavLinkItem to="/bowler" icon={BarChart2}>Bowler</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "baqueira" && (
          <div className="submenu-content">
            <div className="submenu-label">Baqueira</div>
            {canAccess(role, type, category, 'operators') && (
              <NavLinkItem to="/operators" icon={Route}>Agregadores</NavLinkItem>
            )}
            {canAccess(role, type, category, 'rules') && (
              <NavLinkItem to="/rules" icon={PencilRuler}>Reglas de negocio</NavLinkItem>
            )}
            {canAccess(role, type, category, 'articles') && (
              <NavLinkItem to="/articles" icon={Newspaper}>Artículos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'arqueosbaqueira') && (
              <NavLinkItem to="/arqueosbaqueira" icon={Gem}>Arqueos ERP Baqueira</NavLinkItem>
            )}
            {canAccess(role, type, category, 'montarto') && (
              <NavLinkItem to="/hotelrental" icon={Hotel}>Montarto</NavLinkItem>
            )}
            {canAccess(role, type, category, 'ocupationaccumulated') && (
              <NavLinkItem to="/ocupationaccumulated" icon={BarChart2}>Ocupación acumulada</NavLinkItem>
            )}
            {canAccess(role, type, category, 'ocupationdaily') && (
              <NavLinkItem to="/ocupationdaily" icon={BarChart2}>Ocupación diaria</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "soporte" && (
          <div className="submenu-content">
            <div className="submenu-label">Soporte</div>
            {canAccess(role, type, category, 'supportticketadd') && (
              <NavLinkItem to="/supportticketadd" icon={Receipt}>Mis Tickets</NavLinkItem>
            )}
            {canAccess(role, type, category, 'support-chat') && (
              <NavLinkItem to="/support-chat" icon={MessageSquare}>Chat</NavLinkItem>
            )}
            {canAccess(role, type, category, 'faqs') && (
              <>
                <NavLinkItem to="/faqs" icon={HelpCircle}>FAQs</NavLinkItem>
                <NavLinkItem to="/manuals" icon={BookOpen}>Manuales</NavLinkItem>
              </>
            )}
            {canAccess(role, type, category, 'integraciones') && (
              <NavLinkItem to="/integraciones" icon={Newspaper}>Integraciones</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "usuarios" && (
          <div className="submenu-content">
            <div className="submenu-label">Usuarios</div>
            {canAccess(role, type, category, 'my-manager-users') && (
              <NavLinkItem to="/my-manager-users" icon={Users}>Manager</NavLinkItem>
            )}
            {canAccess(role, type, category, 'my-pda-users') && (
              <NavLinkItem to="/my-pda-users" icon={Smartphone}>Operativo</NavLinkItem>
            )}
            {canAccess(role, type, category, 'my-cash-users') && (
              <NavLinkItem to="/my-cash-users" icon={Calculator}>Cash-Control</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "comercial" && (
          <div className="submenu-content">
            <div className="submenu-label">Comercial</div>
            {canAccess(role, type, category, 'sales-modes') && (
              <NavLinkItem to="/sales-modes" icon={Briefcase}>Modos de venta</NavLinkItem>
            )}
            {canAccess(role, type, category, 'ar-viewer') && (
              <NavLinkItem to="/ar-viewer" icon={Box}>Visor AR</NavLinkItem>
            )}
            {canAccess(role, type, category, 'proposal-configurator') && (
              <NavLinkItem to="/proposal-configurator" icon={FileText}>Configurador</NavLinkItem>
            )}
            {canAccess(role, type, category, 'proposals') && (
              <NavLinkItem to="/proposals" icon={FileText}>Propuestas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'proposal-prices') && (
              <NavLinkItem to="/proposal-prices" icon={FileText}>Precios</NavLinkItem>
            )}
            {canAccess(role, type, category, 'replacement-prices') && (
              <NavLinkItem to="/replacements" icon={Replace}>Recambios</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "admin" && (
          <div className="submenu-content">
            <div className="submenu-label">Administración</div>
            {canAccess(role, type, category, 'users') && (
              <NavLinkItem to="/users" icon={UserCog}>Administradores</NavLinkItem>
            )}
            {hasAdminExtras && <div className="submenu-label">Extras</div>}
            {canAccess(role, type, category, 'gameranking') && (
              <NavLinkItem to="/gameranking" icon={Gamepad2}>Juegos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'duplicated-sales') && (
              <NavLinkItem to="/duplicated-sales" icon={AlertTriangle}>Ventas Duplicadas</NavLinkItem>
            )}
            {role === 'ADMIN' && type === 'MASTER' && (
              <NavLinkItem to="/maint-planner" icon={Wrench}>Ventanas de mantenimiento</NavLinkItem>
            )}
          </div>
        )}
      </div>

      {/* Pro License Toast */}
      {proToast && (
        <div className="pro-toast">
          <Lock size={16} />
          <span>Esta función solo está disponible con la <strong>Licencia Pro</strong></span>
        </div>
      )}
      <ProUpgradeModal isOpen={showProModal} onClose={onCloseProModal} onRequest={onProRequest} loading={proUpgradeLoading} sent={proUpgradeSent} error={proUpgradeError} role={role} type={type} category={category} />
    </div>
  );
};

/**
 * Right-side Nightscan Drawer
 */
const NightscanDrawer = ({
  open, onClose, issues, onOpenMachine, onAnalysis
}) => {
  // Export the listed incidences as a real .xlsx (native columns, no CSV
  // separator/locale issues) using the same values shown in the list items
  const downloadExcel = () => {
    const rows = issues.map((machine) => {
      const _nsDate = formatNightscanDate(machine.NightScanTimeStampEnd);
      const date = (_nsDate && !isNaN(_nsDate))
        ? _nsDate.toLocaleString("es-ES", { timeZone: "UTC" })
        : "";
      const lastSignal = machine.Last_signal
        ? new Date(machine.Last_signal).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })
        : "";
      return {
        Serial: machine.Serial,
        Cliente: machine.Customer?.Fullname || "",
        NightScan: date,
        "Última señal": lastSignal,
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows, {
      header: ["Serial", "Cliente", "NightScan", "Última señal"],
    });
    ws["!cols"] = [{ wch: 14 }, { wch: 30 }, { wch: 22 }, { wch: 22 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Nightscan");
    XLSX.writeFile(wb, `nightscan-incidencias-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <>
      <aside className={`rightpanel ${open ? "rightpanel--open" : ""}`}>
        <div className="rightpanel__header">
          <div className="rightpanel__title">Incidencias Nightscan</div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className="nightscan-csv-btn"
              onClick={downloadExcel}
              disabled={issues.length === 0}
              title="Descargar listado en Excel"
            >
              <Download size={14} />
              Excel
            </button>
            <button
              className="nightscan-csv-btn nightscan-csv-btn--analysis"
              onClick={onAnalysis}
              title="Análisis: qué máquinas se han quedado offline o sin nightscan desde el último día laborable"
            >
              <BarChart2 size={14} />
              Análisis
            </button>
          </div>
        </div>
        <div className="rightpanel__body">
          {issues.length === 0 && (
            <div className="rightpanel__empty">Sin incidencias pendientes.</div>
          )}
          {issues.map((machine) => {
            const _nsDate = formatNightscanDate(machine.NightScanTimeStampEnd);
            const date = (_nsDate && !isNaN(_nsDate))
              ? _nsDate.toLocaleString("es-ES", { timeZone: "UTC" })
              : "";
            const lastSignal = machine.Last_signal
              ? new Date(machine.Last_signal).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })
              : "—";
            return (
              <button
                key={machine._id}
                type="button"
                className="rightpanel__item"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onOpenMachine(machine._id);
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <strong>{machine.Serial}</strong>
                </div>
                <div style={{ fontSize: 13, marginTop: 6 }}>
                  <div><small>Cliente:</small> {machine.Customer?.Fullname}</div>
                  <div><small>NightScan:</small> {date}</div>
                  <div><small>Última señal:</small> {lastSignal}</div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>
      {open && <div className="backdrop-right" onClick={onClose} />}
    </>
  );
};

/**
 * Nightscan Analysis Modal
 * Separates NEW problems (appeared since the previous working day — since
 * Friday when reviewing on a Monday) from CHRONIC ones (machines that have
 * been offline / without nightscan since before), so the daily review only
 * needs to focus on what just broke, before the customer notices.
 */
const dayKeyFromParts = ({ y, m, d }) => y * 10000 + m * 100 + d;

const NightscanAnalysisModal = ({ open, onClose, machines, onOpenMachine, loading }) => {
  const analysis = useMemo(() => {
    if (!open) return null;

    const now = new Date();
    const todayParts = getSpainDateParts(now);
    const todayUtcMs = Date.UTC(todayParts.y, todayParts.m - 1, todayParts.d);
    // 0=Sunday, 1=Monday... reviewing on Monday must cover the whole weekend
    const weekdayIdx = new Date(todayUtcMs).getUTCDay();
    const daysBack = weekdayIdx === 1 ? 3 : 1;
    const windowStartDate = new Date(todayUtcMs - daysBack * 24 * 60 * 60 * 1000);
    const windowStartKey =
      windowStartDate.getUTCFullYear() * 10000 +
      (windowStartDate.getUTCMonth() + 1) * 100 +
      windowStartDate.getUTCDate();
    const windowLabel = `${String(windowStartDate.getUTCDate()).padStart(2, "0")}/${String(
      windowStartDate.getUTCMonth() + 1
    ).padStart(2, "0")}`;

    const daysSince = (key) => {
      if (!key) return null;
      const y = Math.floor(key / 10000);
      const m = Math.floor((key % 10000) / 100);
      const d = key % 100;
      return Math.max(0, Math.round((todayUtcMs - Date.UTC(y, m - 1, d)) / 86400000));
    };

    // Current Spain wall-clock minutes, to flag offline machines that may
    // simply be inside their configured sleep window right now
    let nowSpainMinutes = null;
    try {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: SPAIN_TZ, hour: "2-digit", minute: "2-digit", hour12: false,
      }).formatToParts(now);
      const pick = (t) => Number(parts.find((p) => p.type === t)?.value);
      nowSpainMinutes = pick("hour") * 60 + pick("minute");
    } catch (_) {}

    const inSleepWindowNow = (machine) => {
      if (nowSpainMinutes === null) return false;
      const s = machine.SleepStart ?? 0;
      const e = machine.SleepEnd ?? 0;
      const st = Math.floor(s / 100) * 60 + (s % 100);
      const en = Math.floor(e / 100) * 60 + (e % 100);
      if (st === en) return false;
      return st < en
        ? nowSpainMinutes >= st && nowSpainMinutes <= en
        : nowSpainMinutes >= st || nowSpainMinutes <= en;
    };

    const offlineNew = [];
    const offlineChronic = [];
    const noScanNew = [];
    const noScanChronic = [];

    (machines || []).forEach((machine) => {
      if (NIGHTSCAN_EXCLUDED_SERIALS.includes(machine.Serial)) return;
      if (machine.Customer?._id === VACWAY_CUSTOMER_ID) return;

      const lastSignalMs = machine.Last_signal ? Date.parse(machine.Last_signal) : NaN;
      const isOffline =
        !Number.isFinite(lastSignalMs) || (now.getTime() - lastSignalMs) / 1000 >= 60 * 5;
      const signalKey = Number.isFinite(lastSignalMs)
        ? dayKeyFromParts(getSpainDateParts(new Date(lastSignalMs)))
        : null;

      // Same "did last night's scan" rule the drawer badge uses
      const nsDate = formatNightscanDate(machine.NightScanTimeStampEnd);
      const nsValid = nsDate instanceof Date && !isNaN(nsDate);
      const nsKey = nsValid
        ? nsDate.getUTCFullYear() * 10000 + (nsDate.getUTCMonth() + 1) * 100 + nsDate.getUTCDate()
        : null;
      const scanOk = nsValid && isNightscanInSleepPeriod(nsDate, machine.SleepStart, machine.SleepEnd);

      const entry = {
        machine,
        isOffline,
        scanOk,
        sleepNow: isOffline && inSleepWindowNow(machine),
        signalDays: daysSince(signalKey),
        nsDays: daysSince(nsKey),
        nsDate: nsValid ? nsDate : null,
      };

      if (isOffline) {
        if (signalKey !== null && signalKey >= windowStartKey) offlineNew.push(entry);
        else offlineChronic.push(entry);
      }
      if (!scanOk) {
        if (nsKey !== null && nsKey >= windowStartKey) noScanNew.push(entry);
        else noScanChronic.push(entry);
      }
    });

    const byCustomer = (a, b) =>
      (a.machine.Customer?.Fullname || "").localeCompare(b.machine.Customer?.Fullname || "") ||
      (a.machine.Serial || "").localeCompare(b.machine.Serial || "");
    offlineNew.sort(byCustomer);
    offlineChronic.sort(byCustomer);
    noScanNew.sort(byCustomer);
    noScanChronic.sort(byCustomer);

    return {
      offlineNew, offlineChronic, noScanNew, noScanChronic,
      windowLabel,
      includesWeekend: daysBack === 3,
    };
  }, [open, machines]);

  if (!open || !analysis) return null;

  const { offlineNew, offlineChronic, noScanNew, noScanChronic, windowLabel, includesWeekend } = analysis;

  const fmtSignal = (machine) =>
    machine.Last_signal
      ? new Date(machine.Last_signal).toLocaleString("es-ES", { timeZone: SPAIN_TZ })
      : "—";
  const fmtNs = (entry) =>
    entry.nsDate ? entry.nsDate.toLocaleString("es-ES", { timeZone: "UTC" }) : "—";

  const downloadExcel = () => {
    const toRow = (entry, tipo, days) => ({
      Tipo: tipo,
      Cliente: entry.machine.Customer?.Fullname || "",
      Serial: entry.machine.Serial || "",
      "Último NightScan": fmtNs(entry),
      "Última señal": entry.machine.Last_signal ? fmtSignal(entry.machine) : "",
      "Días": days === null ? "" : days,
    });
    const rows = [
      ...offlineNew.map((e) => toRow(e, "Offline (nueva)", e.signalDays)),
      ...noScanNew.map((e) => toRow(e, "Sin nightscan (nueva)", e.nsDays)),
      ...offlineChronic.map((e) => toRow(e, "Offline (crónica)", e.signalDays)),
      ...noScanChronic.map((e) => toRow(e, "Sin nightscan (crónica)", e.nsDays)),
    ];
    const ws = XLSX.utils.json_to_sheet(rows, {
      header: ["Tipo", "Cliente", "Serial", "Último NightScan", "Última señal", "Días"],
    });
    ws["!cols"] = [{ wch: 22 }, { wch: 30 }, { wch: 14 }, { wch: 22 }, { wch: 22 }, { wch: 7 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Análisis");
    XLSX.writeFile(wb, `nightscan-analisis-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const renderItem = (entry, kind) => {
    const { machine } = entry;
    return (
      <button
        key={machine._id}
        type="button"
        className="ns-analysis-item"
        onClick={() => onOpenMachine(machine._id)}
        title="Abrir máquina"
      >
        <div className="ns-analysis-item__main">
          <div className="ns-analysis-item__serial">
            {machine.Serial}
            {kind === "offline" && !entry.scanOk && (
              <span className="ns-analysis-tag ns-analysis-tag--noscan">sin nightscan</span>
            )}
            {kind === "noscan" && entry.isOffline && (
              <span className="ns-analysis-tag ns-analysis-tag--offline">offline</span>
            )}
            {entry.sleepNow && (
              <span className="ns-analysis-tag ns-analysis-tag--sleep">horario sleep</span>
            )}
          </div>
          <div className="ns-analysis-item__customer">{machine.Customer?.Fullname || "—"}</div>
        </div>
        <div className="ns-analysis-item__meta">
          {kind === "offline" ? (
            <>
              <div>Señal: {fmtSignal(machine)}</div>
              {entry.signalDays !== null && entry.signalDays > 0 && (
                <div className="ns-analysis-days">{entry.signalDays} día{entry.signalDays === 1 ? "" : "s"} sin señal</div>
              )}
            </>
          ) : (
            <>
              <div>NightScan: {fmtNs(entry)}</div>
              {entry.nsDays !== null && entry.nsDays > 0 && (
                <div className="ns-analysis-days">{entry.nsDays} día{entry.nsDays === 1 ? "" : "s"} sin scan</div>
              )}
            </>
          )}
        </div>
      </button>
    );
  };

  const renderSection = (title, cls, entries, kind, emptyText) => (
    <div className={`ns-analysis-section ${cls}`}>
      <div className="ns-analysis-section__head">
        <span>{title}</span>
        <span className="ns-analysis-count">{entries.length}</span>
      </div>
      <div className="ns-analysis-list">
        {entries.length === 0 ? (
          <div className="ns-analysis-empty">{emptyText}</div>
        ) : (
          entries.map((entry) => renderItem(entry, kind))
        )}
      </div>
    </div>
  );

  return (
    <div className="ns-analysis-overlay" onClick={onClose}>
      <div className="ns-analysis-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ns-analysis-header">
          <div>
            <h2>Análisis Nightscan / Offline</h2>
            <div className="ns-analysis-sub">
              Problemas nuevos desde el {windowLabel}
              {includesWeekend ? " (incluye el fin de semana)" : ""} · las crónicas llevan así desde antes
            </div>
          </div>
          <div className="ns-analysis-actions">
            <button
              className="nightscan-csv-btn"
              onClick={downloadExcel}
              disabled={
                loading ||
                offlineNew.length + noScanNew.length + offlineChronic.length + noScanChronic.length === 0
              }
              title="Descargar análisis en Excel"
            >
              <Download size={14} />
              Excel
            </button>
            <button className="ns-analysis-close" onClick={onClose} aria-label="Cerrar">
              ✕
            </button>
          </div>
        </div>
        <div className="ns-analysis-body">
          {loading ? (
            <div className="ns-analysis-loading">
              <div className="ns-analysis-loading__spinner" />
              Actualizando señales en tiempo real…
            </div>
          ) : (
            <>
              <div className="ns-analysis-grid">
                {renderSection(
                  "Se han quedado OFFLINE",
                  "ns-analysis-section--offline",
                  offlineNew,
                  "offline",
                  "Ninguna máquina se ha quedado offline en este periodo ✓"
                )}
                {renderSection(
                  "No han hecho el NIGHTSCAN",
                  "ns-analysis-section--noscan",
                  noScanNew,
                  "noscan",
                  "Todas las máquinas activas han hecho el nightscan ✓"
                )}
              </div>
              <div className="ns-analysis-grid">
                {renderSection(
                  <>Offline crónicas <span className="ns-analysis-chronic-note">(desde antes del {windowLabel})</span></>,
                  "ns-analysis-section--chronic",
                  offlineChronic,
                  "offline",
                  "Sin máquinas offline crónicas"
                )}
                {renderSection(
                  <>Sin nightscan crónicas <span className="ns-analysis-chronic-note">(desde antes del {windowLabel})</span></>,
                  "ns-analysis-section--chronic",
                  noScanChronic,
                  "noscan",
                  "Sin máquinas crónicas sin nightscan"
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Mobile Menu Modal - displays navigation items in a bottom sheet
 */
const MobileMenuModal = ({
  open,
  onClose,
  title,
  icon: Icon,
  children,
  isProfile = false,
  fullname,
  role,
  type,
  onOpenProModal,
}) => {
  return (
    <>
      {open && <div className="backdrop" onClick={onClose} />}
      <div className={`mobile-menu-modal ${open ? "open" : ""}`}>
        {isProfile ? (
          <div className="mobile-profile-header">
            <div className="mobile-profile-avatar">
              {role === 'ADMIN' ? (
                <img
                  src={`https://vacway-images.s3.eu-central-1.amazonaws.com/${fullname}.png`}
                  alt={fullname}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://vacway-images.s3.eu-central-1.amazonaws.com/default.png";
                  }}
                />
              ) : (
                <UserIcon size={24} />
              )}
            </div>
            <div className="mobile-profile-info">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div className="mobile-profile-name">{fullname}</div>
                {type === 'LICENCIA_PRO' && (
                  <span style={{ padding: '2px 8px', borderRadius: 20, background: 'linear-gradient(90deg,#22c55e,#16a34a)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>PRO</span>
                )}
                {type === 'LICENCIA_BASICA' && (
                  <span style={{ padding: '2px 8px', borderRadius: 20, background: 'rgba(100,116,139,.3)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>BASIC</span>
                )}
                {type === 'PDA' && (
                  <span style={{ padding: '2px 8px', borderRadius: 20, background: 'linear-gradient(90deg,#3b82f6,#1d4ed8)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>OPERATIVO</span>
                )}
                {type === 'CASH' && (
                  <span style={{ padding: '2px 8px', borderRadius: 20, background: 'linear-gradient(90deg,#10b981,#065f46)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>CASH-CONTROL</span>
                )}
                {type === 'MANAGER' && (
                  <span style={{ padding: '2px 8px', borderRadius: 20, background: 'linear-gradient(90deg,#8b5cf6,#5b21b6)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>MANAGER</span>
                )}
                {type === 'GROUP' && (
                  <span style={{ padding: '2px 8px', borderRadius: 20, background: 'linear-gradient(90deg,#f59e0b,#b45309)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>GROUP</span>
                )}
              </div>
              <div className="mobile-profile-role">{role}</div>
              {type === 'LICENCIA_BASICA' && (
                <button
                  onClick={onOpenProModal}
                  style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, border: 'none', background: '#f59e0b', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                >
                  <Star size={13} />Actualizar a PRO
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="mobile-menu-header">
            <div className="mobile-menu-title">
              {Icon && <Icon size={22} />}
              {title}
            </div>
            <button className="mobile-menu-close" onClick={onClose} aria-label="Cerrar menú">
              <ArrowLeft size={22} />
            </button>
          </div>
        )}
        <div className="mobile-menu-content">
          {children}
        </div>
      </div>
    </>
  );
};

/**
 * Mobile Bottom Navigation Bar
 */
const MobileBottomNav = ({
  role,
  type,
  category,
  onNavigate,
  activeTab,
  setActiveTab,
  nightscanCount,
  openTicketsCount,
  openIncidencesCount,
  fullname,
  onLogout,
  showProModal,
  onOpenProModal,
  onCloseProModal,
  onProRequest,
  proUpgradeLoading,
  proUpgradeSent,
  proUpgradeError,
  onOpenCustomerProfile,
}) => {
  // ============================================
  // CONFIGURATION: Define which sections appear in bottom bar vs "More" menu
  // ============================================
  // Set favoriteMenus to the sections you want in the bottom bar (max 3 recommended)
  // Available options: 'canales', 'gestion', 'reporte', 'operativa', 'sat', 'baqueira', 'soporte', 'admin'
  // Note: 'profile' is always shown as the last tab
  const favoriteMenus = ['canales', 'gestion', 'reporte', 'operativa']; // <-- EDIT THIS to change favorites

  // Check permissions
  const hasCanales = hasAccessToAny(role, type, category, ['machines', 'qrs', 'pdas']);
  const hasGestion = hasAccessToAny(role, type, category, ['lockersale', 'pin', 'redeemcodes', 'wristband-redeemcodes', 'rentals', 'calendars', 'partners', 'discount-codes']);
  const hasReporte = hasAccessToAny(role, type, category, ['pro-dashboard', 'operational-dashboard','occupancy-dashboard','sales', 'tiempos', 'ocupation', 'objectives', 'pinrecovers', 'localizacion', 'withdrawals', 'api', 'dashboard-rental', 'batteries']);
  const hasOperativa = hasAccessToAny(role, type, category, ['customers', 'contracts', 'audit', 'tpvs', 'modules', 'matrixs', 'lowbluetooth', 'lockersqty', 'openings', 'updates', 'software-versions', 'rental-sanity']);
  const hasSAT = hasAccessToAny(role, type, category, ['supporttickets', 'incidences', 'faults', 'replacements', 'slas']);
  const hasBaqueira = hasAccessToAny(role, type, category, ['operators', 'rules', 'articles', 'arqueosbaqueira', 'montarto', 'ocupationaccumulated', 'ocupationdaily']);
  const hasSoporte = hasAccessToAny(role, type, category, ['supportticketadd', 'faqs', 'manuals', 'support-chat', 'integraciones']);
  const hasAdmin = hasAccessToAny(role, type, category, ['users', 'gameranking', 'duplicated-sales']);
  // Items del bloque "Extras" del menú Administración (todo menos Administradores). Si el usuario
  // no puede ver ninguno, se oculta también la etiqueta EXTRAS para no dejarla huérfana.
  const hasAdminExtras = hasAccessToAny(role, type, category, ['gameranking', 'duplicated-sales']);
  const hasComercial = hasAccessToAny(role, type, category, ['sales-modes', 'ar-viewer']);
  const hasUsuarios = hasAccessToAny(role, type, category, ['my-cash-users', 'my-pda-users', 'my-manager-users']);
  const showNightscan = canAccess(role, type, category, 'nightscan');
  const showTicketsIndicator = role === 'ADMIN' && (type === 'MASTER' || type === 'SAT1');
  const showIncidencesIndicator = role === 'ADMIN' && (type === 'MASTER' || type === 'SAT2');

  // Menu sections configuration
  const menuSections = {
    canales: {
      has: hasCanales,
      icon: Monitor,
      label: 'Canales',
      isFavorite: favoriteMenus.includes('canales')
    },
    gestion: {
      has: hasGestion,
      icon: CalendarCheck2,
      label: 'Gestión',
      isFavorite: favoriteMenus.includes('gestion')
    },
    reporte: {
      has: hasReporte,
      icon: BarChart2,
      label: 'Reporte',
      isFavorite: favoriteMenus.includes('reporte')
    },
    operativa: {
      has: hasOperativa,
      icon: SlidersHorizontal,
      label: 'Operativa',
      isFavorite: favoriteMenus.includes('operativa')
    },
    sat: {
      has: hasSAT,
      icon: Headset,
      label: 'SAT',
      isFavorite: favoriteMenus.includes('sat')
    },
    baqueira: {
      has: hasBaqueira,
      icon: Mountain,
      label: 'Baqueira',
      isFavorite: favoriteMenus.includes('baqueira')
    },
    usuarios: {
      has: hasUsuarios,
      icon: Users,
      label: 'Usuarios',
      isFavorite: favoriteMenus.includes('usuarios')
    },
    soporte: {
      has: hasSoporte,
      icon: HelpCircle,
      label: 'Soporte',
      isFavorite: favoriteMenus.includes('soporte')
    },
    comercial: {
      has: hasComercial,
      icon: Briefcase,
      label: 'Comercial',
      isFavorite: favoriteMenus.includes('comercial')
    },
    admin: {
      has: hasAdmin,
      icon: Crown,
      label: 'Admin',
      isFavorite: favoriteMenus.includes('admin')
    }
  };

  // Split menus into favorites (shown in bottom bar) and others (shown in "More")
  const favoriteTabs = Object.entries(menuSections)
    .filter(([key, config]) => config.has && config.isFavorite)
    .map(([key]) => key);

  const moreTabs = Object.entries(menuSections)
    .filter(([key, config]) => config.has && !config.isFavorite)
    .map(([key]) => key);

  const showMoreButton = moreTabs.length > 0;

  // Pro-lock toast state
  const [proToast, setProToast] = React.useState(false);
  const proToastTimer = React.useRef(null);
  const showProToast = () => {
    setProToast(true);
    if (proToastTimer.current) clearTimeout(proToastTimer.current);
    proToastTimer.current = setTimeout(() => setProToast(false), 3500);
  };

  const NavLinkItem = ({ to, icon: Icon, children }) => {
    const feature = to.replace(/^\//, '');
    const locked = isFeatureLocked(role, type, category, feature);

    if (locked) {
      return (
        <div
          className="mobile-menu-item mobile-menu-item--locked"
          onClick={(e) => { e.preventDefault(); onOpenProModal(); }}
          role="button"
          tabIndex={0}
        >
          {Icon && <Icon size={20} />}
          <span>{children}</span>
          <span className="pro-badge-inline">PRO</span>
          <Lock size={14} className="lock-icon-inline" />
        </div>
      );
    }

    // navigate manually instead of relying on NavLink to avoid timing issues
    return (
      <div
        className="mobile-menu-item"
        onClick={() => {
          setActiveTab(null);
          onNavigate(to);
        }}
      >
        {Icon && <Icon size={20} />}
        <span>{children}</span>
      </div>
    );
  };

  return (
    <>
      <nav className="mobile-bottom-nav">
        {/* Favorite tabs (shown directly in bottom bar) */}
        {favoriteTabs.map(tabKey => {
          const config = menuSections[tabKey];
          const Icon = config.icon;
          return (
            <button
              key={tabKey}
              className={`mobile-tab ${activeTab === tabKey ? "active" : ""}`}
              onClick={() => setActiveTab(prev => prev === tabKey ? null : tabKey)}
              aria-label={config.label}
            >
              <Icon size={22} />
              <span className="mobile-tab-label">{config.label}</span>
            </button>
          );
        })}

        {/* More button (contains non-favorite sections) */}
        {showMoreButton && (
          <button
            className={`mobile-tab ${activeTab === "more" ? "active" : ""}`}
            onClick={() => setActiveTab(prev => prev === "more" ? null : "more")}
            aria-label="Más opciones"
          >
            <LayoutGrid size={22} />
            <span className="mobile-tab-label">Más</span>
          </button>
        )}

        {/* Profile tab (always shown) */}
        <button
          className={`mobile-tab ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => setActiveTab(prev => prev === "profile" ? null : "profile")}
          aria-label="Perfil"
        >
          {(() => {
            const totalBadgeCount = 
              (showNightscan ? nightscanCount : 0) + 
              (showTicketsIndicator ? openTicketsCount : 0) + 
              (showIncidencesIndicator ? openIncidencesCount : 0);
            return totalBadgeCount > 0 && (
              <span className="mobile-tab-badge">{totalBadgeCount}</span>
            );
          })()}
          <UserIcon size={22} />
          <span className="mobile-tab-label">Perfil</span>
        </button>
      </nav>

      {/* Modals for each tab */}
      <MobileMenuModal
        open={activeTab === "canales"}
        onClose={() => setActiveTab(null)}
        title="Canales"
        icon={Monitor}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Canales de venta</div>
          {canAccess(role, type, category, 'machines') && (
            <NavLinkItem to="/machines" icon={Monitor}>
              {category === 'BAQUEIRA' ? 'Controladores' : 'Kioscos'}
            </NavLinkItem>
          )}
          {canAccess(role, type, category, 'qrs') && (
            <NavLinkItem to="/qrs" icon={QrCode}>QRs</NavLinkItem>
          )}
          {canAccess(role, type, category, 'pdas') && (
            <NavLinkItem to="/pdas" icon={Smartphone}>PDAs</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "gestion"}
        onClose={() => setActiveTab(null)}
        title="Gestión"
        icon={CalendarCheck2}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Gestión</div>
          {canAccess(role, type, category, 'lockersale') && (
            <NavLinkItem to="/lockersale" icon={CreditCard}>Vender</NavLinkItem>
          )}
          {canAccess(role, type, category, 'pin') && (
            <NavLinkItem to="/pin" icon={Unlock}>Recuperar Pin</NavLinkItem>
          )}
          {canAccess(role, type, category, 'redeemcodes') && (
            <NavLinkItem to="/redeemcodes" icon={Ticket}>Códigos Canjeables</NavLinkItem>
          )}
          {canAccess(role, type, category, 'wristband-redeemcodes') && (
            <NavLinkItem to="/wristband-redeemcodes" icon={KeySquare}>Pulseras RFID</NavLinkItem>
          )}
          {canAccess(role, type, category, 'rentals') && (
            <NavLinkItem to="/rentals" icon={Key}>Reservas</NavLinkItem>
          )}
          {canAccess(role, type, category, 'calendars') && (
            <NavLinkItem to="/calendars" icon={CalendarCheck2}>Calendarios</NavLinkItem>
          )}
          {canAccess(role, type, category, 'email-templates') && (
            <NavLinkItem to="/email-templates" icon={Mail}>Plantillas Email</NavLinkItem>
          )}
          {canAccess(role, type, category, 'partners') && (
            <NavLinkItem to="/partners" icon={UserCog}>Partners</NavLinkItem>
          )}
          {canAccess(role, type, category, 'discount-codes') && (
            <NavLinkItem to="/discount-codes" icon={Tag}>Códigos Descuento</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "reporte"}
        onClose={() => setActiveTab(null)}
        title="Reporte"
        icon={BarChart2}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Reporte</div>
          {canAccess(role, type, category, 'pro-dashboard') && (
            <NavLinkItem to="/pro-dashboard" icon={BarChart2}>Dashboard</NavLinkItem>
          )}
          {canAccess(role, type, category, 'dashboard-rental') && (
            <NavLinkItem to="/dashboard-rental" icon={CalendarCheck2}>Dashboard Reservas</NavLinkItem>
          )}
          {canAccess(role, type, category, 'operational-dashboard') && (
            <NavLinkItem to="/operational-dashboard" icon={BarChart2}>Dashboard Operativo</NavLinkItem>
          )}
          {canAccess(role, type, category, 'occupancy-dashboard') && (
            <NavLinkItem to="/occupancy-dashboard" icon={PieChart}>Dashboard Ocupación</NavLinkItem>
          )}
          {canAccess(role, type, category, 'sales') && (
            <NavLinkItem to="/sales" icon={Filter}>Ventas</NavLinkItem>
          )}
          {canAccess(role, type, category, 'tiempos') && (
            <NavLinkItem to="/tiempos" icon={Timer}>Tiempos</NavLinkItem>
          )}
          {canAccess(role, type, category, 'localizacion') && (
            <NavLinkItem to="/localizacion" icon={Route}>Localización</NavLinkItem>
          )}
          {canAccess(role, type, category, 'objectives') && (
            <NavLinkItem to="/objectives" icon={Target}>Objetivos</NavLinkItem>
          )}
          {canAccess(role, type, category, 'pinrecovers') && (
            <NavLinkItem to="/pinrecovers" icon={Lock}>Recuperación pines</NavLinkItem>
          )}
          {canAccess(role, type, category, 'withdrawals') && (
            <NavLinkItem to="/withdrawals" icon={Calculator}>Retiros</NavLinkItem>
          )}
          {canAccess(role, type, category, 'api') && (
            <NavLinkItem to="/api" icon={Code}>API</NavLinkItem>
          )}
          {canAccess(role, type, category, 'activity-dashboard') && (
            <NavLinkItem to="/activity-dashboard" icon={Users}>Actividad</NavLinkItem>
          )}
          {canAccess(role, type, category, 'batteries') && (
            <NavLinkItem to="/batteries" icon={BatteryFull}>Baterías</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "operativa"}
        onClose={() => setActiveTab(null)}
        title="Operativa"
        icon={SlidersHorizontal}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Operativa</div>
          {canAccess(role, type, category, 'customers') && (
            <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
          )}
          {canAccess(role, type, category, 'contracts') && (
            <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
          )}
          {canAccess(role, type, category, 'audit') && (
            <NavLinkItem to="/audit" icon={Database}>Audit</NavLinkItem>
          )}
          {canAccess(role, type, category, 'tpvs') && (
            <NavLinkItem to="/tpvs" icon={Smartphone}>TPVs</NavLinkItem>
          )}
          {canAccess(role, type, category, 'modules') && (
            <NavLinkItem to="/modules" icon={LayoutGrid}>Módulos</NavLinkItem>
          )}
          {canAccess(role, type, category, 'modules') && (
            <NavLinkItem to="/change-sn" icon={Replace}>Cambiar Locker</NavLinkItem>
          )}
          {canAccess(role, type, category, 'matrixs') && (
            <NavLinkItem to="/matrixs" icon={Download}>Firmware</NavLinkItem>
          )}
          {canAccess(role, type, category, 'lowbluetooth') && (
            <NavLinkItem to="/lowbluetooth" icon={Wifi}>Bluetooth</NavLinkItem>
          )}
          {canAccess(role, type, category, 'lockersqty') && (
            <NavLinkItem to="/lockersqty" icon={BarChart2}>Recurrencia</NavLinkItem>
          )}
          {canAccess(role, type, category, 'openings') && (
            <NavLinkItem to="/openings" icon={DoorOpen}>Aperturas</NavLinkItem>
          )}
          {canAccess(role, type, category, 'updates') && (
            <NavLinkItem to="/updates" icon={RefreshCw}>Status Updates</NavLinkItem>
          )}
          {canAccess(role, type, category, 'software-versions') && (
            <NavLinkItem to="/software-versions" icon={Code}>Versiones SW</NavLinkItem>
          )}
          {canAccess(role, type, category, 'rental-sanity') && (
            <NavLinkItem to="/rental-sanity" icon={ClipboardCheck}>Rental Sanity</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "sat"}
        onClose={() => setActiveTab(null)}
        title="SAT"
        icon={Headset}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">SAT</div>
          {canAccess(role, type, category, 'supporttickets') && (
            <NavLinkItem to="/supporttickets" icon={Receipt}>Tickets</NavLinkItem>
          )}
          {canAccess(role, type, category, 'incidences') && (
            <NavLinkItem to="/incidences" icon={SlidersHorizontal}>Incidéncias</NavLinkItem>
          )}
          {canAccess(role, type, category, 'faults') && (
            <NavLinkItem to="/faults" icon={Info}>Averías</NavLinkItem>
          )}
          {canAccess(role, type, category, 'replacements') && (
            <NavLinkItem to="/replacements" icon={SlidersHorizontal}>Recambios</NavLinkItem>
          )}
          {canAccess(role, type, category, 'slas') && (
            <NavLinkItem to="/slas" icon={ShieldCheck}>SLA</NavLinkItem>
          )}
          {canAccess(role, type, category, 'bowler') && (
            <NavLinkItem to="/bowler" icon={BarChart2}>Bowler</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "baqueira"}
        onClose={() => setActiveTab(null)}
        title="Baqueira"
        icon={Mountain}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Baqueira</div>
          {canAccess(role, type, category, 'operators') && (
            <NavLinkItem to="/operators" icon={Route}>Agregadores</NavLinkItem>
          )}
          {canAccess(role, type, category, 'rules') && (
            <NavLinkItem to="/rules" icon={PencilRuler}>Reglas de negocio</NavLinkItem>
          )}
          {canAccess(role, type, category, 'articles') && (
            <NavLinkItem to="/articles" icon={Newspaper}>Artículos</NavLinkItem>
          )}
          {canAccess(role, type, category, 'arqueosbaqueira') && (
            <NavLinkItem to="/arqueosbaqueira" icon={Gem}>Arqueos ERP Baqueira</NavLinkItem>
          )}
          {canAccess(role, type, category, 'montarto') && (
            <NavLinkItem to="/hotelrental" icon={Hotel}>Montarto</NavLinkItem>
          )}
          {canAccess(role, type, category, 'ocupationaccumulated') && (
            <NavLinkItem to="/ocupationaccumulated" icon={BarChart2}>Ocupación acumulada</NavLinkItem>
          )}
          {canAccess(role, type, category, 'ocupationdaily') && (
            <NavLinkItem to="/ocupationdaily" icon={BarChart2}>Ocupación diaria</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "soporte"}
        onClose={() => setActiveTab(null)}
        title="Soporte"
        icon={HelpCircle}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Soporte</div>
          {canAccess(role, type, category, 'supportticketadd') && (
            <NavLinkItem to="/supportticketadd" icon={Receipt}>Mis Tickets</NavLinkItem>
          )}
          {canAccess(role, type, category, 'support-chat') && (
            <NavLinkItem to="/support-chat" icon={MessageSquare}>Chat</NavLinkItem>
          )}
          {canAccess(role, type, category, 'faqs') && (
            <>
              <NavLinkItem to="/faqs" icon={HelpCircle}>FAQs</NavLinkItem>
              <NavLinkItem to="/manuals" icon={BookOpen}>Manuales</NavLinkItem>
            </>
          )}
          {canAccess(role, type, category, 'integraciones') && (
            <NavLinkItem to="/integraciones" icon={Newspaper}>Integraciones</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "comercial"}
        onClose={() => setActiveTab(null)}
        title="Comercial"
        icon={Briefcase}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Comercial</div>
          {canAccess(role, type, category, 'sales-modes') && (
            <NavLinkItem to="/sales-modes" icon={Briefcase}>Modos de venta</NavLinkItem>
          )}
          {canAccess(role, type, category, 'ar-viewer') && (
            <NavLinkItem to="/ar-viewer" icon={Box}>Visor AR</NavLinkItem>
          )}
          {canAccess(role, type, category, 'proposal-configurator') && (
            <NavLinkItem to="/proposal-configurator" icon={FileText}>Configurador</NavLinkItem>
          )}
          {canAccess(role, type, category, 'proposals') && (
            <NavLinkItem to="/proposals" icon={FileText}>Propuestas</NavLinkItem>
          )}
          {canAccess(role, type, category, 'proposal-prices') && (
            <NavLinkItem to="/proposal-prices" icon={FileText}>Precios</NavLinkItem>
          )}
          {canAccess(role, type, category, 'replacement-prices') && (
            <NavLinkItem to="/replacements" icon={Replace}>Recambios</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "admin"}
        onClose={() => setActiveTab(null)}
        title="Administración"
        icon={Crown}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Administración</div>
          {canAccess(role, type, category, 'users') && (
            <NavLinkItem to="/users" icon={UserCog}>Administradores</NavLinkItem>
          )}
          {hasAdminExtras && <div className="mobile-menu-section-title">Extras</div>}
          {canAccess(role, type, category, 'gameranking') && (
            <NavLinkItem to="/gameranking" icon={Gamepad2}>Juegos</NavLinkItem>
          )}
          {canAccess(role, type, category, 'duplicated-sales') && (
            <NavLinkItem to="/duplicated-sales" icon={AlertTriangle}>Ventas Duplicadas</NavLinkItem>
          )}
          {role === 'ADMIN' && type === 'MASTER' && (
            <NavLinkItem to="/maint-planner" icon={Wrench}>Ventanas de mantenimiento</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      {/* More modal - dynamically shows non-favorite sections */}
      <MobileMenuModal
        open={activeTab === "more"}
        onClose={() => setActiveTab(null)}
        title="Más opciones"
        icon={LayoutGrid}
      >
        {/* Show each non-favorite section that user has access to */}
        {moreTabs.includes('canales') && hasCanales && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Canales de venta</div>
            {canAccess(role, type, category, 'machines') && (
              <NavLinkItem to="/machines" icon={Monitor}>
                {category === 'BAQUEIRA' ? 'Controladores' : 'Kioscos'}
              </NavLinkItem>
            )}
            {canAccess(role, type, category, 'qrs') && (
              <NavLinkItem to="/qrs" icon={QrCode}>QRs</NavLinkItem>
            )}
            {canAccess(role, type, category, 'pdas') && (
              <NavLinkItem to="/pdas" icon={Smartphone}>PDAs</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('gestion') && hasGestion && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Gestión</div>
            {canAccess(role, type, category, 'lockersale') && (
              <NavLinkItem to="/lockersale" icon={CreditCard}>Vender</NavLinkItem>
            )}
            {canAccess(role, type, category, 'pin') && (
              <NavLinkItem to="/pin" icon={Unlock}>Recuperar Pin</NavLinkItem>
            )}
            {canAccess(role, type, category, 'redeemcodes') && (
              <NavLinkItem to="/redeemcodes" icon={Ticket}>Códigos Canjeables</NavLinkItem>
            )}
            {canAccess(role, type, category, 'wristband-redeemcodes') && (
              <NavLinkItem to="/wristband-redeemcodes" icon={KeySquare}>Pulseras RFID</NavLinkItem>
            )}
            {canAccess(role, type, category, 'rentals') && (
              <NavLinkItem to="/rentals" icon={Key}>Reservas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'calendars') && (
              <NavLinkItem to="/calendars" icon={CalendarCheck2}>Calendarios</NavLinkItem>
            )}
            {canAccess(role, type, category, 'email-templates') && (
              <NavLinkItem to="/email-templates" icon={Mail}>Plantillas Email</NavLinkItem>
            )}
            {canAccess(role, type, category, 'partners') && (
              <NavLinkItem to="/partners" icon={UserCog}>Partners</NavLinkItem>
            )}
            {canAccess(role, type, category, 'discount-codes') && (
              <NavLinkItem to="/discount-codes" icon={Tag}>Códigos Descuento</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('reporte') && hasReporte && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Reporte</div>
            {canAccess(role, type, category, 'pro-dashboard') && (
              <NavLinkItem to="/pro-dashboard" icon={BarChart2}>Dashboard</NavLinkItem>
            )}
            {canAccess(role, type, category, 'dashboard-rental') && (
              <NavLinkItem to="/dashboard-rental" icon={CalendarCheck2}>Dashboard Reservas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'operational-dashboard') && (
              <NavLinkItem to="/operational-dashboard" icon={BarChart2}>Dashboard Operativo</NavLinkItem>
            )}
            {canAccess(role, type, category, 'occupancy-dashboard') && (
              <NavLinkItem to="/occupancy-dashboard" icon={PieChart}>Dashboard Ocupación</NavLinkItem>
            )}
            {canAccess(role, type, category, 'sales') && (
              <NavLinkItem to="/sales" icon={Filter}>Ventas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'tiempos') && (
              <NavLinkItem to="/tiempos" icon={Timer}>Tiempos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'localizacion') && (
              <NavLinkItem to="/localizacion" icon={Route}>Localización</NavLinkItem>
            )}
            {canAccess(role, type, category, 'objectives') && (
              <NavLinkItem to="/objectives" icon={Target}>Objetivos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'pinrecovers') && (
              <NavLinkItem to="/pinrecovers" icon={Lock}>Recuperación pines</NavLinkItem>
            )}
            {canAccess(role, type, category, 'withdrawals') && (
              <NavLinkItem to="/withdrawals" icon={Calculator}>Retiros</NavLinkItem>
            )}
            {canAccess(role, type, category, 'api') && (
              <NavLinkItem to="/api" icon={Code}>API</NavLinkItem>
            )}
            {canAccess(role, type, category, 'activity-dashboard') && (
              <NavLinkItem to="/activity-dashboard" icon={Users}>Actividad</NavLinkItem>
            )}
            {canAccess(role, type, category, 'batteries') && (
              <NavLinkItem to="/batteries" icon={BatteryFull}>Baterías</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('operativa') && hasOperativa && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Operativa</div>
            {canAccess(role, type, category, 'customers') && (
              <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
            )}
            {canAccess(role, type, category, 'contracts') && (
              <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'audit') && (
              <NavLinkItem to="/audit" icon={Database}>Audit</NavLinkItem>
            )}
            {canAccess(role, type, category, 'tpvs') && (
              <NavLinkItem to="/tpvs" icon={Smartphone}>TPVs</NavLinkItem>
            )}
            {canAccess(role, type, category, 'modules') && (
              <NavLinkItem to="/modules" icon={LayoutGrid}>Módulos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'modules') && (
              <NavLinkItem to="/change-sn" icon={Replace}>Cambiar Locker</NavLinkItem>
            )}
            {canAccess(role, type, category, 'matrixs') && (
              <NavLinkItem to="/matrixs" icon={Download}>Firmware</NavLinkItem>
            )}
            {canAccess(role, type, category, 'lowbluetooth') && (
              <NavLinkItem to="/lowbluetooth" icon={Wifi}>Bluetooth</NavLinkItem>
            )}
            {canAccess(role, type, category, 'lockersqty') && (
              <NavLinkItem to="/lockersqty" icon={BarChart2}>Recurrencia</NavLinkItem>
            )}
            {canAccess(role, type, category, 'openings') && (
              <NavLinkItem to="/openings" icon={DoorOpen}>Aperturas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'updates') && (
              <NavLinkItem to="/updates" icon={RefreshCw}>Status Updates</NavLinkItem>
            )}
            {canAccess(role, type, category, 'software-versions') && (
              <NavLinkItem to="/software-versions" icon={Code}>Versiones SW</NavLinkItem>
            )}
            {canAccess(role, type, category, 'rental-sanity') && (
              <NavLinkItem to="/rental-sanity" icon={ClipboardCheck}>Rental Sanity</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('sat') && hasSAT && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">SAT</div>
            {canAccess(role, type, category, 'supporttickets') && (
              <NavLinkItem to="/supporttickets" icon={Receipt}>Tickets</NavLinkItem>
            )}
            {canAccess(role, type, category, 'incidences') && (
              <NavLinkItem to="/incidences" icon={SlidersHorizontal}>Incidéncias</NavLinkItem>
            )}
            {canAccess(role, type, category, 'faults') && (
              <NavLinkItem to="/faults" icon={Info}>Averías</NavLinkItem>
            )}
            {canAccess(role, type, category, 'replacements') && (
              <NavLinkItem to="/replacements" icon={SlidersHorizontal}>Recambios</NavLinkItem>
            )}
            {canAccess(role, type, category, 'slas') && (
              <NavLinkItem to="/slas" icon={ShieldCheck}>SLA</NavLinkItem>
            )}
            {canAccess(role, type, category, 'bowler') && (
              <NavLinkItem to="/bowler" icon={BarChart2}>Bowler</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('baqueira') && hasBaqueira && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Baqueira</div>
            {canAccess(role, type, category, 'operators') && (
              <NavLinkItem to="/operators" icon={Route}>Agregadores</NavLinkItem>
            )}
            {canAccess(role, type, category, 'rules') && (
              <NavLinkItem to="/rules" icon={PencilRuler}>Reglas de negocio</NavLinkItem>
            )}
            {canAccess(role, type, category, 'articles') && (
              <NavLinkItem to="/articles" icon={Newspaper}>Artículos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'arqueosbaqueira') && (
              <NavLinkItem to="/arqueosbaqueira" icon={Gem}>Arqueos ERP Baqueira</NavLinkItem>
            )}
            {canAccess(role, type, category, 'montarto') && (
              <NavLinkItem to="/hotelrental" icon={Hotel}>Montarto</NavLinkItem>
            )}
            {canAccess(role, type, category, 'ocupationaccumulated') && (
              <NavLinkItem to="/ocupationaccumulated" icon={BarChart2}>Ocupación acumulada</NavLinkItem>
            )}
            {canAccess(role, type, category, 'ocupationdaily') && (
              <NavLinkItem to="/ocupationdaily" icon={BarChart2}>Ocupación diaria</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('usuarios') && hasUsuarios && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Usuarios</div>
            {canAccess(role, type, category, 'my-manager-users') && (
              <NavLinkItem to="/my-manager-users" icon={Users}>Manager</NavLinkItem>
            )}
            {canAccess(role, type, category, 'my-pda-users') && (
              <NavLinkItem to="/my-pda-users" icon={Smartphone}>Operativo</NavLinkItem>
            )}
            {canAccess(role, type, category, 'my-cash-users') && (
              <NavLinkItem to="/my-cash-users" icon={Calculator}>Cash-Control</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('soporte') && hasSoporte && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Soporte</div>
            {canAccess(role, type, category, 'supportticketadd') && (
              <NavLinkItem to="/supportticketadd" icon={Receipt}>Mis Tickets</NavLinkItem>
            )}
            {canAccess(role, type, category, 'support-chat') && (
              <NavLinkItem to="/support-chat" icon={MessageSquare}>Chat</NavLinkItem>
            )}
            {canAccess(role, type, category, 'faqs') && (
              <>
                <NavLinkItem to="/faqs" icon={HelpCircle}>FAQs</NavLinkItem>
                <NavLinkItem to="/manuals" icon={BookOpen}>Manuales</NavLinkItem>
              </>
            )}
            {canAccess(role, type, category, 'integraciones') && (
              <NavLinkItem to="/integraciones" icon={Newspaper}>Integraciones</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('comercial') && hasComercial && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Comercial</div>
            {canAccess(role, type, category, 'sales-modes') && (
              <NavLinkItem to="/sales-modes" icon={Briefcase}>Modos de venta</NavLinkItem>
            )}
            {canAccess(role, type, category, 'ar-viewer') && (
              <NavLinkItem to="/ar-viewer" icon={Box}>Visor AR</NavLinkItem>
            )}
            {canAccess(role, type, category, 'proposal-configurator') && (
              <NavLinkItem to="/proposal-configurator" icon={FileText}>Configurador</NavLinkItem>
            )}
            {canAccess(role, type, category, 'proposals') && (
              <NavLinkItem to="/proposals" icon={FileText}>Propuestas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'proposal-prices') && (
              <NavLinkItem to="/proposal-prices" icon={FileText}>Precios</NavLinkItem>
            )}
            {canAccess(role, type, category, 'replacement-prices') && (
              <NavLinkItem to="/replacements" icon={Replace}>Recambios</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('admin') && hasAdmin && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Administración</div>
            {canAccess(role, type, category, 'users') && (
              <NavLinkItem to="/users" icon={UserCog}>Administradores</NavLinkItem>
            )}
            {hasAdminExtras && <div className="mobile-menu-section-title">Extras</div>}
            {canAccess(role, type, category, 'gameranking') && (
              <NavLinkItem to="/gameranking" icon={Gamepad2}>Juegos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'duplicated-sales') && (
              <NavLinkItem to="/duplicated-sales" icon={AlertTriangle}>Ventas Duplicadas</NavLinkItem>
            )}
            {role === 'ADMIN' && type === 'MASTER' && (
              <NavLinkItem to="/maint-planner" icon={Wrench}>Ventanas de mantenimiento</NavLinkItem>
            )}
          </div>
        )}
      </MobileMenuModal>

      <ProUpgradeModal isOpen={showProModal} onClose={onCloseProModal} onRequest={onProRequest} loading={proUpgradeLoading} sent={proUpgradeSent} error={proUpgradeError} role={role} type={type} category={category} />

      <MobileMenuModal
        open={activeTab === "profile"}
        onClose={() => setActiveTab(null)}
        title="Perfil"
        isProfile={true}
        fullname={fullname}
        role={role}
        type={type}
        onOpenProModal={() => { setActiveTab(null); onNavigate('/planes'); }}
      >
        <div className="mobile-menu-section">
          {/* Mi cuenta for CUSTOMER role */}
          {role === 'CUSTOMER' && onOpenCustomerProfile && (
            <>
              <div className="mobile-menu-section-title">Cuenta</div>
              <button
                className="mobile-menu-item"
                onClick={() => { setActiveTab(null); onOpenCustomerProfile(); }}
              >
                <UserIcon size={20} />
                <span>Mi cuenta</span>
              </button>
              <div className="mobile-menu-divider" />
            </>
          )}
          {/* Nightscan, Tickets, Incidencias - always show if user has access */}
          {(showNightscan || showTicketsIndicator || showIncidencesIndicator) && (
            <>
              <div className="mobile-menu-section-title">Notificaciones</div>
              
              {showNightscan && (
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    setActiveTab(null);
                    onNavigate('nightscan');
                  }}
                >
                  <Info size={20} />
                  <span>Incidencias Nightscan</span>
                  {nightscanCount > 0 && (
                    <span className="mobile-tab-badge" style={{ position: 'static', marginLeft: 'auto' }}>
                      {nightscanCount}
                    </span>
                  )}
                </button>
              )}
              
              {showTicketsIndicator && (
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    setActiveTab(null);
                    onNavigate('/supporttickets');
                  }}
                >
                  <Ticket size={20} />
                  <span>Tickets de Soporte</span>
                  {openTicketsCount > 0 && (
                    <span className="mobile-tab-badge" style={{ position: 'static', marginLeft: 'auto', background: '#3b82f6' }}>
                      {openTicketsCount}
                    </span>
                  )}
                </button>
              )}
              
              {showIncidencesIndicator && (
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    setActiveTab(null);
                    onNavigate('/incidences');
                  }}
                >
                  <AlertTriangle size={20} />
                  <span>Incidencias</span>
                  {openIncidencesCount > 0 && (
                    <span className="mobile-tab-badge" style={{ position: 'static', marginLeft: 'auto', background: '#f97316' }}>
                      {openIncidencesCount}
                    </span>
                  )}
                </button>
              )}
              
              <div className="mobile-menu-divider" />
            </>
          )}

          <div className="mobile-menu-section-title">Configuración</div>

          <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <SlidersHorizontal size={20} style={{ color: 'var(--sidebar-icon-color)' }} />
              <span style={{ color: 'var(--sidebar-text-secondary)', fontSize: 15 }}>Tema</span>
            </div>
            <DarkModeToggle />
          </div>

          <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <LayoutGrid size={20} style={{ color: 'var(--sidebar-icon-color)' }} />
              <span style={{ color: 'var(--sidebar-text-secondary)', fontSize: 15 }}>Idioma</span>
            </div>
            <div style={{ position: 'relative', zIndex: 20 }}>
              <GoogleTranslate />
            </div>
          </div>

          <div className="mobile-menu-divider" />

          <button className="mobile-menu-item" onClick={onLogout}>
            <LogOut size={20} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </MobileMenuModal>

      {/* Pro License Toast */}
      {proToast && (
        <div className="pro-toast pro-toast--mobile">
          <Lock size={16} />
          <span>Esta función solo está disponible con la <strong>Licencia Pro</strong></span>
        </div>
      )}
    </>
  );
};

/**
 * AppShell
 */
const AppShellBase = ({
  children,
  fullname,
  role,
  type,
  category,
  authLogout,
  fetchMachinesNightscan,
  fetchOpenTicketsCount,
  fetchOpenIncidencesCount,
  machinesNightscan,
  openTicketsCount,
  openIncidencesCount,
  push,
  requestProUpgrade,
  resetProUpgrade,
  proUpgradeLoading,
  proUpgradeSent,
  proUpgradeError,
  fetchCustomerProfile,
  resetCustomerProfile,
  customerProfileLoading,
  customerProfileData,
  customerProfileError,
  nightscanLoading,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 768px)").matches);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [showProModal, setShowProModal] = useState(false);
  const [customerProfileOpen, setCustomerProfileOpen] = useState(false);

  const handleProRequest = () => requestProUpgrade();
  const handleProClose = () => { setShowProModal(false); resetProUpgrade(); };

  // NEW: right panel Nightscan state
  const [nightscanOpen, setNightscanOpen] = useState(false);

  // Nightscan analysis full modal (opened from the drawer's Análisis button)
  const [nightscanAnalysisOpen, setNightscanAnalysisOpen] = useState(false);

  // NEW: Command Palette state
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  // NEW: Mobile bottom nav tab state
  const [mobileActiveTab, setMobileActiveTab] = useState(null);

  // Game modals state
  const [gamePickerOpen, setGamePickerOpen]     = useState(false);
  const [minesweeperOpen, setMinesweeperOpen]   = useState(false);
  const [flappyBirdOpen, setFlappyBirdOpen]     = useState(false);
  const [snakeOpen, setSnakeOpen]               = useState(false);

  const handleGameSelect = (gameId) => {
    setGamePickerOpen(false);
    if (gameId === 'minesweeper') setMinesweeperOpen(true);
    if (gameId === 'flappybird')  setFlappyBirdOpen(true);
    if (gameId === 'snake')       setSnakeOpen(true);
  };

  // Handle user button click - show game picker for ADMIN, profile for CUSTOMER
  const handleUserClick = () => {
    if (role === 'ADMIN') {
      setGamePickerOpen(true);
    } else if (role === 'CUSTOMER') {
      fetchCustomerProfile();
      setCustomerProfileOpen(true);
    }
  };

  useEffect(() => { injectStyles(); }, []);

  // Command Palette keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Viewport watcher
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
    };
  }, []);

  // Collapse the left menu ALWAYS after any navigation
  const onNavigate = (action) => {
    setCollapsed(true);        // collapse hamburger after navigating
    setNightscanOpen(false);   // optional: also close right panel

    // Handle route navigation for both desktop and mobile
    if (action && action.startsWith('/')) {
      push(action);
    }

    if (isMobile) {
      setMobileOpen(false);
      setMobileActiveTab(null); // close mobile bottom nav modals

      // Handle special actions from mobile menu
      if (action === 'nightscan') {
        setNightscanOpen(true);
      }
    }
  };

  // Open machine from the right drawer -> also collapse left
  const openMachine = (id) => {
    push(`/machines/${id}`);
    setNightscanOpen(false);
    setNightscanAnalysisOpen(false);
    setMobileOpen(false);
    setCollapsed(true);      // ensure left menu collapsed
  };

  // Análisis button: close the lateral drawer and open the full analysis modal.
  // Always refetch: the cached nightscan data has Last_signal frozen at fetch
  // time, and the offline analysis needs real-time signals.
  const handleOpenNightscanAnalysis = () => {
    setNightscanOpen(false);
    setNightscanAnalysisOpen(true);
    fetchMachinesNightscan && fetchMachinesNightscan();
  };

  // Initial load / auth rule and fetch
  useEffect(() => {
    if (fullname === 'Joaquin Abellan') {
      authLogout();
      push('/login');
    }

    if (role === 'ADMIN' && (type === 'MASTER' || type === 'SAT1')) {
      fetchOpenTicketsCount && fetchOpenTicketsCount();
    }
    if (role === 'ADMIN' && (type === 'MASTER' || type === 'SAT2')) {
      fetchOpenIncidencesCount && fetchOpenIncidencesCount();
    }
  }, [fullname, authLogout, push, fetchOpenTicketsCount, fetchOpenIncidencesCount, role, type]);

  // Nightscan cache key: represents the "data session" — data is final after 08:00 Spain time.
  // Before 08:00 we're still on the previous night's data, so we use yesterday's date as key.
  // This ensures that if the user loads before 08:00 and then again after 08:00, a new fetch happens.
  const nightscanCacheKey = (() => {
    const now = new Date();
    const spainHour = Number(
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Madrid', hour: 'numeric', hour12: false })
        .format(now)
    );
    // If before 08:00 Spain time, the valid data window is still "yesterday morning's run"
    const effectiveDate = spainHour < 8
      ? new Date(now.getTime() - 24 * 60 * 60 * 1000)
      : now;
    const { y, m, d } = getSpainDateParts(effectiveDate);
    return `nightscan_${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  })();

  // Seed local state from localStorage cache immediately (before any fetch)
  const [nightscanData, setNightscanData] = useState(() => {
    try {
      const raw = localStorage.getItem(nightscanCacheKey);
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return null;
  });

  // Fetch from API only when no cache exists for today
  useEffect(() => {
    if (!canAccess(role, type, category, 'nightscan')) return;
    if (nightscanData !== null) return; // cache hit — skip network
    fetchMachinesNightscan && fetchMachinesNightscan();
  }, [role, type, category]); // eslint-disable-line react-hooks/exhaustive-deps

  // When Redux receives fresh API data, persist to localStorage and update local state
  useEffect(() => {
    if (!machinesNightscan) return;
    try {
      localStorage.setItem(nightscanCacheKey, JSON.stringify(machinesNightscan));
    } catch (_) {}
    setNightscanData(machinesNightscan);
  }, [machinesNightscan]); // eslint-disable-line react-hooks/exhaustive-deps

  //useEffect(() => { localStorage.setItem("sidebarCollapsed", collapsed ? "1" : "0"); }, [collapsed]);

  useEffect(() => {
    if (isMobile && nightscanOpen && mobileOpen) {
      setMobileOpen(false);
    }
  }, [isMobile, nightscanOpen, mobileOpen]);

  // Compute issues with same rules as legacy (exclude serials + sleep period)
  const nightscanIssues = useMemo(() => {
    const currentYear = getSpainDateParts(new Date()).y;
    const list = (nightscanData || []).filter(machine => {
      const date = formatNightscanDate(machine.NightScanTimeStampEnd);
      // Skip machines whose last nightscan is not from the current year
      // (offline since a previous year and still not operative).
      // Date is built with Date.UTC so getUTCFullYear() == Spain year.
      if (!date || !(date instanceof Date) || isNaN(date)) return false;
      if (date.getUTCFullYear() !== currentYear) return false;
      const notSleeping = !isNightscanInSleepPeriod(date, machine.SleepStart, machine.SleepEnd);
      const notExcluded = !NIGHTSCAN_EXCLUDED_SERIALS.includes(machine.Serial);
      const notVacway = machine.Customer?._id !== VACWAY_CUSTOMER_ID;
      return notSleeping && notExcluded && notVacway;
    });
    // Sort by Serial number alphabetically
    list.sort((a, b) => (a.Serial || '').localeCompare(b.Serial || ''));
    return list;
  }, [nightscanData]);

  const nightscanCount = nightscanIssues.length;

  const onLogout = () => { authLogout(); push('/login'); };

  // Open/close the RIGHT Nightscan drawer; if opening, collapse/close LEFT menu
  const handleToggleNightscan = () => {
    setNightscanOpen(prev => {
      const next = !prev;
      if (next) {
        // Ensure LEFT is closed when RIGHT opens
        if (isMobile) {
          setMobileOpen(false);     // close mobile left drawer
        } else {
          setCollapsed(true);       // collapse left on desktop
        }
      }
      return next;
    });
  };

  // Toggle LEFT on mobile; if opening, close RIGHT
  const handleToggleSidebarMobile = () => {
    setMobileOpen(prev => {
      const next = !prev;
      if (next) {
        setNightscanOpen(false);    // close right when left opens
      }
      return next;
    });
  };

  // Toggle LEFT on desktop; when expanding (not collapsed), close RIGHT
  const handleToggleDesktopSidebar = () => {
    setCollapsed(prev => {
      const next = !prev;           // next=false means expanded; next=true means collapsed
      if (!isMobile && !next) {
        setNightscanOpen(false);    // expanded left => close right
      }
      return next;
    });
  };



  return (
    <ToastProvider>
      <div className="shell">
        <TopBar
          fullname={fullname}
          onLogout={onLogout}
          isMobile={isMobile}
          role={role}
          type={type}
          category={category}
          nightscanCount={nightscanCount}
          nightscanLoading={nightscanLoading}
          openTicketsCount={openTicketsCount}
          openIncidencesCount={openIncidencesCount}
          onToggleNightscan={handleToggleNightscan}
          onToggleSidebarMobile={handleToggleSidebarMobile}
          onOpenCommandPalette={() => setCommandPaletteOpen(true)}
          onUserClick={handleUserClick}
          push={push}
          showProModal={showProModal}
          onOpenProModal={() => setShowProModal(true)}
          onCloseProModal={handleProClose}
          onProRequest={handleProRequest}
          proUpgradeLoading={proUpgradeLoading}
          proUpgradeSent={proUpgradeSent}
          proUpgradeError={proUpgradeError}
        />

        {/* Command Palette */}
        <CommandPalette
          isOpen={commandPaletteOpen}
          onClose={() => setCommandPaletteOpen(false)}
          role={role}
          type={type}
          category={category}
          canAccess={canAccess}
          hasAccessToAny={hasAccessToAny}
          push={push}
        />

        {/* === Desktop Sidebar === */}
        {!isMobile && (
          <div className="sidebar-wrapper">
            <Sidebar
              role={role}
              type={type}
              category={category}
              onNavigate={onNavigate}
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              showProModal={showProModal}
              onOpenProModal={() => setShowProModal(true)}
              onCloseProModal={handleProClose}
              onProRequest={handleProRequest}
              proUpgradeLoading={proUpgradeLoading}
              proUpgradeSent={proUpgradeSent}
              proUpgradeError={proUpgradeError}
            />
          </div>
        )}

        <main className={`content ${!isMobile ? (activeMenu ? 'shifted' : 'not-shifted') : ''}`}>
          {/* Breadcrumbs */}
          <Breadcrumbs category={category} />

          {children}
        </main>

        {/* Mobile Bottom Navigation */}
        {isMobile && (
          <MobileBottomNav
            role={role}
            type={type}
            category={category}
            onNavigate={onNavigate}
            activeTab={mobileActiveTab}
            setActiveTab={setMobileActiveTab}
            nightscanCount={nightscanCount}
            openTicketsCount={openTicketsCount}
            openIncidencesCount={openIncidencesCount}
            fullname={fullname}
            onLogout={onLogout}
            showProModal={showProModal}
            onOpenProModal={() => setShowProModal(true)}
            onCloseProModal={handleProClose}
            onProRequest={handleProRequest}
            proUpgradeLoading={proUpgradeLoading}
            proUpgradeSent={proUpgradeSent}
            proUpgradeError={proUpgradeError}
            onOpenCustomerProfile={role === 'CUSTOMER' ? () => { fetchCustomerProfile(); setCustomerProfileOpen(true); } : undefined}
          />
        )}

        {/* Right-side Nightscan drawer */}
        {role === "ADMIN" && (
          <NightscanDrawer
            open={nightscanOpen}
            onClose={() => setNightscanOpen(false)}
            issues={nightscanIssues}
            onOpenMachine={openMachine}
            onAnalysis={handleOpenNightscanAnalysis}
          />
        )}

        {/* Full-screen Nightscan analysis modal */}
        {role === "ADMIN" && (
          <NightscanAnalysisModal
            open={nightscanAnalysisOpen}
            onClose={() => setNightscanAnalysisOpen(false)}
            machines={nightscanData || []}
            onOpenMachine={openMachine}
            loading={nightscanLoading}
          />
        )}

        {/* Admin arcade — game picker */}
        <GamePicker
          isOpen={gamePickerOpen}
          onSelect={handleGameSelect}
          onClose={() => setGamePickerOpen(false)}
        />

        {/* Minesweeper modal for ADMIN users */}
        <Minesweeper
          isOpen={minesweeperOpen}
          toggle={() => setMinesweeperOpen(false)}
        />

        {/* Flappy Locker modal for ADMIN users */}
        <FlappyBird
          isOpen={flappyBirdOpen}
          toggle={() => setFlappyBirdOpen(false)}
        />

        {/* Snake Locker modal for ADMIN users */}
        <Snake
          isOpen={snakeOpen}
          toggle={() => setSnakeOpen(false)}
        />

        <FloatingToolsButton />

        {/* Customer profile modal */}
        <CustomerProfileModal
          isOpen={customerProfileOpen}
          onClose={() => { setCustomerProfileOpen(false); resetCustomerProfile(); }}
          loading={customerProfileLoading}
          data={customerProfileData}
          error={customerProfileError}
        />
      </div>
    </ToastProvider>
  );
};

const mapStateToProps = (state) => ({
  fullname: state.auth.fullname,
  role: state.auth.role,
  type: state.auth.type,
  category: state.auth.category,
  machinesNightscan: state.machines.machinesNightscan,
  nightscanLoading: state.machines.nightscanLoading,
  openTicketsCount: state.supporttickets.openTicketsCount,
  openIncidencesCount: state.incidences.openIncidencesCount,
  proUpgradeLoading: state.license.loading,
  proUpgradeSent: state.license.sent,
  proUpgradeError: state.license.error,
  customerProfileLoading: state.customerProfile.loading,
  customerProfileData: state.customerProfile.data,
  customerProfileError: state.customerProfile.error,
});

const mapDispatchToProps = (dispatch) => ({
  authLogout: bindActionCreators(authLogout, dispatch),
  fetchMachinesNightscan: bindActionCreators(fetchMachinesNightscan, dispatch),
  fetchOpenTicketsCount: bindActionCreators(fetchOpenTicketsCount, dispatch),
  fetchOpenIncidencesCount: bindActionCreators(fetchOpenIncidencesCount, dispatch),
  push: bindActionCreators(push, dispatch),
  requestProUpgrade: bindActionCreators(requestProUpgrade, dispatch),
  resetProUpgrade: bindActionCreators(resetProUpgrade, dispatch),
  fetchCustomerProfile: bindActionCreators(fetchCustomerProfile, dispatch),
  resetCustomerProfile: bindActionCreators(resetCustomerProfile, dispatch),
});

export const AppShell = connect(mapStateToProps, mapDispatchToProps)(AppShellBase);
